import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BragCardCanvas } from './DesignStudio';
import { hasCardVerifyObject, globalImageAspectCache, getIntrinsicAspectFromSrc } from './TemplateDesigner';
import { formatDateTimeValue, getPickerTypeForFormat, getDefaultFormat, toPickerInputValue } from '../utils/dateTimeFormatter';
import { QRCodeSVG } from '../utils/qrCode';
import html2canvas from 'html2canvas';
import MapCoordinatePicker from './MapCoordinatePicker';
import { formatCoordinates, isCoordinateField } from '../utils/coordinateUtils';
import { extractExifFromBlob } from '../utils/exifReader';
import { useTheme } from '../context/ThemeContext';
import MobileCardStatsModal from './mobile/MobileCardStatsModal';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '');

// Safe clipboard copy function resilient to HTTP / non-secure / Safari mobile LAN contexts
const safeCopyToClipboard = async (text) => {
  if (!text) return false;
  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('[Clipboard] navigator.clipboard.writeText failed, using fallback:', err);
    }
  }
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '-9999px';
    textArea.style.left = '-9999px';
    textArea.style.opacity = '0';
    textArea.setAttribute('readonly', '');
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return Boolean(successful);
  } catch (err) {
    console.warn('[Clipboard] execCommand fallback failed:', err);
    return false;
  }
};

// Safe reverse geocode helper to resolve place names from GPS coordinates
const reverseGeocodePlace = async (lat, lon) => {
  if (lat === null || lat === undefined || lon === null || lon === undefined) return null;
  try {
    const parsedLat = parseFloat(lat);
    const parsedLon = parseFloat(lon);
    if (isNaN(parsedLat) || isNaN(parsedLon)) return null;

    let signal;
    if (typeof AbortController !== 'undefined') {
      const controller = new AbortController();
      setTimeout(() => controller.abort(), 3500);
      signal = controller.signal;
    }

    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${encodeURIComponent(parsedLat)}&lon=${encodeURIComponent(parsedLon)}&zoom=10`,
      { signal }
    );
    if (resp.ok) {
      const data = await resp.json();
      const addr = data.address || {};
      const locality = addr.city || addr.town || addr.village || addr.suburb || addr.municipality || addr.county || '';
      const state = addr.state || addr.province || addr.region || '';
      const country = addr.country || '';
      const parts = [locality, state, country].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : (data.display_name?.split(',').slice(0, 3).join(', ') || null);
    }
  } catch (err) {
    console.warn('[ReverseGeocode] place lookup error/timeout:', err);
  }
  return null;
};

// Clean, monochrome neutral SVG icons (replaces colorful emoji)
const NeutralIcons = {
  Share: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  ),
  Copy: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  Globe: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Mail: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  CalendarBooking: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <path d="M10 14l2 2 4-4" />
    </svg>
  ),
  QrCodeIcon: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  Video: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  ),
  WhatsApp: ({ size = 16, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
  XTwitter: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  ),
  Facebook: ({ size = 15, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  ),
  Telegram: ({ size = 15, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  ),
  Message: ({ size = 15, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  Flip: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
    </svg>
  ),
  Image: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  Eye: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Move: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="5 9 2 12 5 15" />
      <polyline points="9 5 12 2 15 5" />
      <polyline points="15 19 12 22 9 19" />
      <polyline points="19 9 22 12 19 15" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="12" y1="2" x2="12" y2="22" />
    </svg>
  ),
  Crop: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M6 2v14a2 2 0 0 0 2 2h14" />
      <path d="M18 22V8a2 2 0 0 0-2-2H2" />
    </svg>
  ),
  Lock: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Link: ({ size = 11, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Clock: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Calendar: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Alert: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Check: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Coin: ({ size = 20, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v10M9 10h6" />
    </svg>
  ),
  Shield: ({ size = 14, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  ArrowDown: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  ),
  ArrowLeft: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  ArrowRight: ({ size = 13, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Close: ({ size = 11, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ChevronLeft: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  ChevronRight: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ChevronUp: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  ChevronDown: ({ size = 12, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  Spinner: ({ size = 32, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite', ...style }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
};

// Proportionally scaled 3D Flippable Card component for both Mobile Wizard & Customer Views
function ResponsiveFlippableCard({
  template,
  cardData,
  isFlipped,
  onFlip,
  frontCardRef,
  backCardRef,
  datasets,
  showStamp = false,
  user = null,
  orgData = null,
  tenantConfig = null,
  isPlacingPicture = false,
  onPlacePicture = null,
  onPlacementChange = null,
  activeImageElem = null,
  onVerify = null,
  verifying = false,
  onLinkClick = null,
  isSubscriptionExpired = false
}) {
  const containerRef = useRef(null);
  const dragStartRef = useRef(null);
  const dims = template?.cardDimensions || { width: 400, height: 560 };
  const cardWidth = dims.width || 400;
  const cardHeight = dims.height || 560;

  const [availableWidth, setAvailableWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return Math.max(260, Math.min(window.innerWidth - 32, 440));
    }
    return cardWidth;
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      if (el && el.clientWidth > 0) {
        setAvailableWidth(el.clientWidth);
      }
    };
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0) {
            setAvailableWidth(entry.contentRect.width);
          }
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    } else {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
  }, []);

  // Compute proportional scale factor to fit within container
  const maxUsableWidth = Math.max(200, availableWidth);
  const scale = Math.min(1, maxUsableWidth / cardWidth);
  const scaledWidth = Math.round(cardWidth * scale);
  const scaledHeight = Math.round(cardHeight * scale);

  const handlePointerDown = (e) => {
    if (!isPlacingPicture || !onPlacementChange) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {}
    dragStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialPanX: activeImageElem?.imgPanX || 0,
      initialPanY: activeImageElem?.imgPanY || 0
    };
  };

  const handlePointerMove = (e) => {
    if (!isPlacingPicture || !dragStartRef.current || !onPlacementChange) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - dragStartRef.current.startX;
    const dy = e.clientY - dragStartRef.current.startY;
    
    const effScale = scale || 1;
    const frameW = (activeImageElem?.width || cardWidth * 0.7) * effScale;
    const frameH = (activeImageElem?.height || cardHeight * 0.5) * effScale;
    
    const deltaPercentX = (dx / (frameW || 1)) * 100;
    const deltaPercentY = (dy / (frameH || 1)) * 100;
    
    const newPanX = Math.round(Math.max(-100, Math.min(100, dragStartRef.current.initialPanX + deltaPercentX)));
    const newPanY = Math.round(Math.max(-100, Math.min(100, dragStartRef.current.initialPanY + deltaPercentY)));
    
    onPlacementChange({ imgPanX: newPanX, imgPanY: newPanY });
  };

  const handlePointerUp = (e) => {
    if (dragStartRef.current) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {}
      dragStartRef.current = null;
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      {/* Tap card to flip hint rendered ABOVE the card */}
      {!isPlacingPicture && (
        <div style={{
          fontSize: '12px',
          color: '#94a3b8',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '10px',
          userSelect: 'none',
          opacity: 0.9
        }}>
          <NeutralIcons.Flip size={13} style={{ flexShrink: 0 }} />
          <span>Tap card to view {isFlipped ? 'Front' : 'Back'} side</span>
        </div>
      )}

      {/* Allocated DOM box matching exact scaled dimensions */}
      <div
        style={{
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          maxWidth: '100%',
          position: 'relative',
          margin: '0 auto 10px auto',
          flexShrink: 0,
          overflow: 'visible',
          animation: showStamp ? 'cardStruckBounce 0.75s cubic-bezier(0.2, 0.8, 0.2, 1) both' : 'none'
        }}
      >
        {/* Scaled coordinate container */}
        <div
          onClick={isPlacingPicture ? undefined : onFlip}
          style={{
            width: `${cardWidth}px`,
            height: `${cardHeight}px`,
            position: 'absolute',
            top: 0,
            left: 0,
            transform: scale !== 1 ? `scale(${scale})` : undefined,
            transformOrigin: 'top left',
            perspective: '1000px',
            WebkitPerspective: '1000px',
            cursor: isPlacingPicture ? 'grab' : 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
        >
          {/* Stamp & Impact Keyframes */}
          <style>{`
            @keyframes stampSlamIn {
              0% {
                transform: translate(-50%, -50%) translate3d(0, -220px, 350px) scale(3.2) rotate(-34deg);
                opacity: 0;
                filter: blur(8px);
              }
              25% {
                opacity: 0.95;
                filter: blur(2px);
              }
              40% {
                transform: translate(-50%, -50%) translate3d(0, 0, 0) scale(0.91) rotate(-11deg);
                opacity: 1;
                filter: blur(0px);
                box-shadow: 0 4px 14px rgba(16, 185, 129, 0.9), inset 0 0 16px rgba(255, 255, 255, 0.7);
              }
              56% {
                transform: translate(-50%, -50%) translate3d(0, -9px, 20px) scale(1.07) rotate(-13.5deg);
                box-shadow: 0 20px 48px rgba(16, 185, 129, 0.5), inset 0 0 12px rgba(255, 255, 255, 0.4);
              }
              72% {
                transform: translate(-50%, -50%) translate3d(0, 0, 0) scale(0.985) rotate(-11.8deg);
              }
              88% {
                transform: translate(-50%, -50%) translate3d(0, -2px, 5px) scale(1.01) rotate(-12.1deg);
              }
              100% {
                transform: translate(-50%, -50%) translate3d(0, 0, 0) scale(1) rotate(-12deg);
                opacity: 1;
                box-shadow: 0 12px 40px rgba(16, 185, 129, 0.6), inset 0 0 12px rgba(255, 255, 255, 0.4);
              }
            }

            @keyframes cardStruckBounce {
              0%, 34% {
                transform: scale(1) translateY(0);
                filter: brightness(1);
              }
              40% {
                transform: scale(0.93) translateY(8px);
                filter: brightness(1.08);
              }
              56% {
                transform: scale(1.03) translateY(-4px);
                filter: brightness(1);
              }
              72% {
                transform: scale(0.99) translateY(2px);
              }
              88% {
                transform: scale(1.005) translateY(-0.5px);
              }
              100% {
                transform: scale(1) translateY(0);
              }
            }

            @keyframes stampShockwave {
              0% {
                transform: translate(-50%, -50%) rotate(-12deg) scale(0.7);
                opacity: 0.85;
                border-width: 4px;
              }
              100% {
                transform: translate(-50%, -50%) rotate(-12deg) scale(2.4);
                opacity: 0;
                border-width: 1px;
              }
            }
          `}</style>

          {/* Authentic Overlay Banner Stamp & Shockwave */}
          {showStamp && (
            <>
              {/* Expanding Shockwave on Impact */}
              <div style={{
                position: 'absolute',
                top: '35%',
                left: '50%',
                width: '180px',
                height: '80px',
                borderRadius: '16px',
                border: '3px solid rgba(52, 211, 153, 0.85)',
                transform: 'translate(-50%, -50%) rotate(-12deg)',
                pointerEvents: 'none',
                zIndex: 9998,
                animation: 'stampShockwave 0.65s 0.28s cubic-bezier(0.1, 0.7, 0.1, 1) both'
              }} />

              {/* Physical Stamp Slamming Down from Above */}
              <div style={{
                position: 'absolute',
                top: '35%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-12deg)',
                zIndex: 9999,
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95), rgba(5, 150, 105, 0.95))',
                color: '#ffffff',
                border: '3px solid #ffffff',
                padding: '12px 28px',
                borderRadius: '12px',
                boxShadow: '0 12px 40px rgba(16, 185, 129, 0.6), inset 0 0 12px rgba(255,255,255,0.4)',
                textAlign: 'center',
                pointerEvents: 'none',
                animation: 'stampSlamIn 0.75s cubic-bezier(0.2, 0.8, 0.2, 1) both',
                transformOrigin: 'center center'
              }}>
                <div style={{ fontSize: '22px', fontWeight: 900, letterSpacing: '3px', textTransform: 'uppercase' }}>
                  AUTHENTIC
                </div>
                <div style={{ fontSize: '10px', fontWeight: 700, opacity: 0.9, letterSpacing: '1px' }}>
                  TROPHYCOIN VERIFIED SEAL
                </div>
              </div>
            </>
          )}

          {/* 3D Rotator - matching TemplateDesigner.js */}
          <div
            style={{
              width: '100%',
              height: '100%',
              transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              transformStyle: 'preserve-3d',
              WebkitTransformStyle: 'preserve-3d',
              position: 'relative',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              WebkitTransform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Front Side */}
            <div
              ref={frontCardRef}
              onPointerDown={isPlacingPicture ? handlePointerDown : undefined}
              onPointerMove={isPlacingPicture ? handlePointerMove : undefined}
              onPointerUp={isPlacingPicture ? handlePointerUp : undefined}
              onPointerCancel={isPlacingPicture ? handlePointerUp : undefined}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(0deg) translateZ(1px)',
                WebkitTransform: 'rotateY(0deg) translateZ(1px)',
                transformStyle: 'flat',
                WebkitTransformStyle: 'flat',
                cursor: isPlacingPicture ? 'grab' : 'pointer',
                touchAction: isPlacingPicture ? 'none' : 'auto',
                zIndex: isFlipped ? 1 : 2,
                pointerEvents: isFlipped ? 'none' : 'auto'
              }}
            >
              <BragCardCanvas
                design={template}
                side="front"
                testDataOverrides={cardData}
                datasets={datasets}
                user={user}
                orgData={orgData}
                tenantConfig={tenantConfig}
                id="coin-card-side-front"
                onVerify={onVerify}
                verifying={verifying}
                onLinkClick={onLinkClick}
                isSubscriptionExpired={isSubscriptionExpired}
              />
            </div>

            {/* Back Side */}
            <div
              ref={backCardRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg) translateZ(1px)',
                WebkitTransform: 'rotateY(180deg) translateZ(1px)',
                transformStyle: 'flat',
                WebkitTransformStyle: 'flat',
                zIndex: isFlipped ? 2 : 1,
                pointerEvents: isFlipped ? 'auto' : 'none'
              }}
            >
              <BragCardCanvas
                design={template}
                side="back"
                testDataOverrides={cardData}
                datasets={datasets}
                user={user}
                orgData={orgData}
                tenantConfig={tenantConfig}
                id="coin-card-side-back"
                onVerify={onVerify}
                verifying={verifying}
                onLinkClick={onLinkClick}
                isSubscriptionExpired={isSubscriptionExpired}
              />
            </div>
          </div>
        </div>
      </div>

      {isPlacingPicture && (
        <div style={{
          fontSize: '11px',
          color: '#38bdf8',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontWeight: 600,
          background: 'rgba(56, 189, 248, 0.12)',
          padding: '4px 12px',
          borderRadius: '12px',
          border: '1px solid rgba(56, 189, 248, 0.3)',
          marginTop: '6px'
        }}>
          <NeutralIcons.Move size={13} style={{ flexShrink: 0 }} />
          <span>Drag photo directly on card or use controls below</span>
        </div>
      )}
    </div>
  );
}

export default function MobileCoinCardView({ publicCode: propPublicCode, onBack, user: propUser = null, initialShowStats = false }) {
  const public_code = propPublicCode || (() => {
    const match = window.location.pathname.match(/\/c\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : '';
  })();

  const themeCtx = useTheme();
  const tenantConfig = themeCtx?.tenantConfig;

  // Stats Modal state
  const [showStatsModal, setShowStatsModal] = useState(Boolean(initialShowStats));

  // Core coin state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [coin, setCoin] = useState(null);

  // Operator Auth state (for unissued coins)
  const [operatorToken, setOperatorToken] = useState(() => {
    if (typeof window === 'undefined') return '';
    return sessionStorage.getItem('token') || localStorage.getItem('cm_operator_token') || localStorage.getItem('token') || '';
  });
  const [authChecking, setAuthChecking] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState('');
  const [operatorUser, setOperatorUser] = useState(null);
  const [orgData, setOrgData] = useState(null);

  // Determine if viewing as an operator/admin
  const isOperatorUser = Boolean(
    (propUser && ['OPERATOR', 'TENANT_ADMIN', 'GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD'].includes(propUser.role)) ||
    (operatorUser && ['OPERATOR', 'TENANT_ADMIN', 'GLOBAL_ADMIN', 'GLOBAL_POWER', 'GLOBAL_STANDARD'].includes(operatorUser.role))
  );

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [tenantLogoFailed, setTenantLogoFailed] = useState(false);

  // Wizard state (for unissued coins)
  const [wizardPages, setWizardPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [issuingLoading, setIssuingLoading] = useState(false);
  const [issuingError, setIssuingError] = useState('');
  const [datasets, setDatasets] = useState({});
  const [validationError, setValidationError] = useState('');
  const [imageExifMap, setImageExifMap] = useState({});

  // Coordinate Picker & Geolocation state
  const [activeMapField, setActiveMapField] = useState(null);
  const [locatingFieldKey, setLocatingFieldKey] = useState(null);

  // 3D Preview state
  const [isFlipped, setIsFlipped] = useState(false);
  const [isPlacingPicture, setIsPlacingPicture] = useState(false);

  // Reset picture placement mode when changing wizard steps
  useEffect(() => {
    setIsPlacingPicture(false);
  }, [currentPageIndex]);

  // Front image elements for picture placement in 3D preview
  const frontImageElements = useMemo(() => {
    const rawSnap = coin?.card_template_snapshot;
    const snap = rawSnap?.config || rawSnap || {};
    const elements = snap?.sides?.front?.elements || [];
    return elements.filter((el) => 
      el.type === 'image' || 
      el.type === 'photo' || 
      el.type === 'picture' || 
      el.type === 'frame' || 
      el.imageType === 'frame' ||
      el.imageVariable ||
      el.src
    );
  }, [coin]);

  const activeImageElem = useMemo(() => {
    // 1. Check frontImageElements with uploaded image in formData
    if (frontImageElements && frontImageElements.length > 0) {
      const mappedElem = frontImageElements.find((el) => {
        const varName = (el.imageVariable || '').replace(/^\$/, '');
        return varName && formData[varName];
      });
      if (mappedElem) return mappedElem;
      const frameElem = frontImageElements.find((el) => el.imageType === 'frame');
      if (frameElem) return frameElem;
      return frontImageElements[0];
    }
    // 2. Fallback: inspect any elements on front side
    const rawSnap = coin?.card_template_snapshot;
    const snap = rawSnap?.config || rawSnap || {};
    const allFront = snap?.sides?.front?.elements || [];
    return allFront.find(el => el.type === 'image' || el.imageType === 'frame' || el.type === 'photo') || allFront[0] || null;
  }, [frontImageElements, formData, coin]);

  const handleUpdatePlacement = useCallback((updates) => {
    setCoin((prevCoin) => {
      if (!prevCoin) return prevCoin;
      const snapshot = prevCoin.card_template_snapshot || {};
      const config = snapshot.config || snapshot;
      const frontSide = config.sides?.front;
      if (!frontSide || !Array.isArray(frontSide.elements)) return prevCoin;

      let found = false;
      const updatedElements = frontSide.elements.map((elem) => {
        const isTarget = activeImageElem 
          ? (elem.id === activeImageElem.id || (!elem.id && elem === activeImageElem))
          : (elem.type === 'image' || elem.imageType === 'frame' || elem.type === 'photo');
        if (isTarget && !found) {
          found = true;
          return {
            ...elem,
            cropApplied: true,
            ...updates
          };
        }
        return elem;
      });

      // If no target was matched, apply to the first element
      if (!found && updatedElements.length > 0) {
        updatedElements[0] = {
          ...updatedElements[0],
          cropApplied: true,
          ...updates
        };
      }

      const updatedSides = {
        ...config.sides,
        front: {
          ...frontSide,
          elements: updatedElements
        }
      };

      const updatedSnapshot = snapshot.config
        ? { ...snapshot, config: { ...snapshot.config, sides: updatedSides } }
        : { ...snapshot, sides: updatedSides };

      return {
        ...prevCoin,
        card_template_snapshot: updatedSnapshot
      };
    });
  }, [activeImageElem]);


  // Verification state (for issued coins)
  const [verifying, setVerifying] = useState(false);
  const [showStamp, setShowStamp] = useState(false);
  const [stampTimeoutId, setStampTimeoutId] = useState(null);
  const [verifyError, setVerifyError] = useState('');

  // Share & Engagement tracking state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const trackedViewRef = useRef(false);

  // Card claiming workflow state
  const [claiming, setClaiming] = useState(false);
  const [claimSuccess, setClaimSuccess] = useState(null);
  const [claimError, setClaimError] = useState(null);

  const handleRequestClaim = async () => {
    if (!public_code) return;
    setClaiming(true);
    setClaimSuccess(null);
    setClaimError(null);
    try {
      const token = typeof window !== 'undefined' ? (sessionStorage.getItem('token') || localStorage.getItem('token') || '') : '';
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/c/${public_code}/request-claim`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to dispatch claim verification token.');
      }
      setClaimSuccess(data);
    } catch (err) {
      setClaimError(err.message || 'Error requesting claim verification code.');
    } finally {
      setClaiming(false);
    }
  };

  // Public engagement tracking beacon
  const trackEvent = useCallback(async (eventType, details = {}) => {
    if (!public_code) return;
    try {
      await fetch(`${API_BASE}/api/v1/coin-vault/${public_code}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          source: details.source || null,
          label: details.label || null,
          destination_url: details.destination_url || null
        })
      });
    } catch (e) {
      console.warn(`[Analytics] Failed tracking ${eventType}:`, e);
    }
  }, [public_code]);

  // Automated view tracking on issued coin load (excluding admin preview mode)
  useEffect(() => {
    if (!coin || !coin.is_issued || trackedViewRef.current) return;
    const search = window.location.search || '';
    if (search.includes('admin_preview=true') || search.includes('preview=true')) {
      return;
    }
    trackedViewRef.current = true;

    // Parse platform attribution from URL params (?src=whatsapp, ?src=x, etc.)
    const params = new URLSearchParams(search);
    const rawSrc = (params.get('src') || params.get('ref') || params.get('utm_source') || '').toLowerCase().trim();

    if (rawSrc) {
      const platformLabels = {
        whatsapp: 'Shared View via WhatsApp',
        x: 'Shared View via X (Twitter)',
        twitter: 'Shared View via X (Twitter)',
        facebook: 'Shared View via Facebook',
        fb: 'Shared View via Facebook',
        telegram: 'Shared View via Telegram',
        sms: 'Shared View via SMS / Messages',
        messages: 'Shared View via SMS / Messages',
        email: 'Shared View via Email',
        copy: 'Shared View via Copied Link',
        native: 'Shared View via Phone Share Sheet',
        qrcode: 'Shared View via Screen QR Scan',
        share: 'Shared Link Opened'
      };
      const label = platformLabels[rawSrc] || `Shared View via ${rawSrc.toUpperCase()}`;
      trackEvent('shared_view', { source: rawSrc, label });
    } else {
      trackEvent('view', { source: 'direct_nfc', label: 'Direct RFID / NFC Scan' });
    }
  }, [coin, trackEvent]);

  // Defensive error suppressor for harmless third-party / Safari WebKit script errors on mobile
  useEffect(() => {
    const handleGlobalError = (event) => {
      const msg = event?.message || (event?.reason && event.reason.message) || '';
      if (
        msg.includes('Script error.') ||
        msg.includes('AbortError') ||
        msg.includes('share') ||
        msg.includes('clipboard')
      ) {
        console.warn('[MobileCoinCardView] Handled non-fatal browser error:', msg);
        if (event.preventDefault) event.preventDefault();
        if (event.stopPropagation) event.stopPropagation();
      }
    };
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleGlobalError);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleGlobalError);
    };
  }, []);

  const frontCardRef = useRef(null);
  const backCardRef = useRef(null);
  const wizardScrollRef = useRef(null);
  const [hasScrollOverflow, setHasScrollOverflow] = useState(false);

  // Check if wizard form fields vertically overflow the scrollable container
  const checkScrollOverflow = useCallback(() => {
    const el = wizardScrollRef.current;
    if (!el) {
      setHasScrollOverflow(false);
      return;
    }
    const canScroll = (el.scrollHeight - el.scrollTop - el.clientHeight) > 15;
    setHasScrollOverflow(canScroll);
  }, []);

  // Smoothly scroll down when user taps the floating indicator
  const handleScrollDownIndicator = () => {
    const el = wizardScrollRef.current;
    if (!el) return;
    const scrollAmount = Math.max(180, Math.round(el.clientHeight * 0.65));
    el.scrollBy({
      top: scrollAmount,
      behavior: 'smooth'
    });
    setTimeout(checkScrollOverflow, 350);
  };

  // Responsive device width tracking
  const [windowWidth, setWindowWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 390));
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobilePhone = windowWidth <= 480;

  // Reset scroll and re-evaluate overflow when step changes or permissions update
  useEffect(() => {
    if (wizardScrollRef.current) {
      wizardScrollRef.current.scrollTop = 0;
    }
    checkScrollOverflow();
    const timer = setTimeout(checkScrollOverflow, 100);
    return () => clearTimeout(timer);
  }, [currentPageIndex, isAuthorized, checkScrollOverflow]);

  // Re-check overflow on viewport changes, validation errors, or form inputs
  useEffect(() => {
    checkScrollOverflow();
    const timer = setTimeout(checkScrollOverflow, 120);
    return () => clearTimeout(timer);
  }, [windowWidth, validationError, formData, checkScrollOverflow]);

  // Use ResizeObserver for dynamic field height/DOM changes
  useEffect(() => {
    const el = wizardScrollRef.current;
    if (!el) return;
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        checkScrollOverflow();
      });
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, [isAuthorized, checkScrollOverflow]);

  // --- Fetch Coin Data ---
  const fetchCoin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/${public_code}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Coin not found');
      }
      setCoin(data);
      if (data.datasets) {
        setDatasets(data.datasets);
      }
      if (!data.is_issued && operatorToken) {
        checkOperatorAuth(operatorToken, data.datasets, data.card_template_snapshot);
      }
    } catch (err) {
      setError(err.message || 'Failed to load coin information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (public_code) {
      fetchCoin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [public_code]);

  // --- Operator / Owner Auth Check ---
  const checkOperatorAuth = async (tokenToUse, fallbackDatasets = null, fallbackTemplate = null) => {
    setAuthChecking(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/${public_code}/check-auth`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenToUse}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (data.authorized) {
        setIsAuthorized(true);
        setAuthError('');
        setOperatorUser(data.user);
        setOrgData(data.orgData);
        const effectiveDatasets = data.datasets || fallbackDatasets || datasets || coin?.datasets || {};
        const effectiveTemplate = data.card_template_snapshot || fallbackTemplate || coin?.card_template_snapshot;
        if (data.datasets) {
          setDatasets(data.datasets);
        }
        if (data.card_template_snapshot) {
          setCoin(prev => prev ? { ...prev, card_template_snapshot: data.card_template_snapshot } : prev);
        }
        initializeWizard(data.prefilled || {}, effectiveDatasets, effectiveTemplate, data.user, data.orgData);
        return true;
      } else {
        setIsAuthorized(false);
        setOperatorUser(null);
        setOrgData(null);
        setAuthError(data.message || 'You do not have permission to log card data for this coin.');
        return false;
      }
    } catch (err) {
      setIsAuthorized(false);
      setAuthError('Authentication verification failed.');
      return false;
    } finally {
      setAuthChecking(false);
    }
  };

  // --- Operator / Owner Login ---
  const handleOperatorLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    setAuthError('');
    try {
      // First attempt primary tenant authentication endpoint (works for all roles: owners, members, operators, admins)
      let res = await fetch(`${API_BASE}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': coin?.tenant?.domain_name || window.location.hostname
        },
        body: JSON.stringify({
          email: loginEmail.trim(),
          password: loginPassword
        })
      });

      if (!res.ok) {
        // Fallback to production-line auth endpoint if primary is not matched
        res = await fetch(`${API_BASE}/api/v1/production-line/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: loginEmail.trim(),
            password: loginPassword,
            tenant_domain: coin?.tenant?.domain_name || window.location.hostname
          })
        });
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Login failed. Please verify your email and password.');
      }
      const token = data.access_token;
      const isAuthed = await checkOperatorAuth(token);
      if (isAuthed) {
        setOperatorToken(token);
        localStorage.setItem('cm_operator_token', token);
      } else {
        // Clear token state so the user is not locked into an unauthorized session
        setOperatorToken('');
        localStorage.removeItem('cm_operator_token');
        setLoginPassword('');
      }
    } catch (err) {
      setLoginError(err.message || 'Authentication failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleOperatorLogout = () => {
    setOperatorToken('');
    localStorage.removeItem('cm_operator_token');
    setIsAuthorized(false);
    setOperatorUser(null);
    setAuthError('');
    setLoginError('');
  };

  // --- Wizard Construction ---
  const initializeWizard = (prefilled = {}, datasetsSource = null, templateSource = null, passedUser = null, passedOrg = null) => {
    const template = templateSource || coin?.card_template_snapshot;
    if (!template) {
      setWizardPages([]);
      return;
    }

    const currentDatasets = datasetsSource || datasets || coin?.datasets || {};
    const linkedDatasets = template.linkedDatasets || [];
    const enabledFields = template.enabledFields || {};
    const hiddenFields = template.hidden_fields || template.hiddenFields || {};
    const formDatasetOrder = template.form_dataset_order || [];
    const fieldMappings = template.field_mappings || {};
    const lockedOptions = template.locked_options || {};
    const requiredFields = template.required_fields || {};
    const dateFormats = template.date_formats || template.field_date_formats || {};

    // 1. Helper to traverse template elements and collect dynamic fields defined on the card template
    const getDynamicFieldsInTemplate = () => {
      const list = [];
      const seen = new Set();

      const add = (datasetKey, label, type, sampleVal) => {
        if (!label || seen.has(label)) return;
        seen.add(label);

        let fieldDef = null;
        let foundDatasetKey = datasetKey;

        if (datasetKey && currentDatasets[datasetKey]) {
          fieldDef = currentDatasets[datasetKey].fields?.find(f => f.label === label);
        } else if (currentDatasets) {
          for (const dsId of Object.keys(currentDatasets)) {
            const f = currentDatasets[dsId]?.fields?.find(item => item.label === label);
            if (f) {
              fieldDef = f;
              foundDatasetKey = dsId;
              break;
            }
          }
        }

        list.push({
          datasetKey: foundDatasetKey || '',
          label,
          alias: fieldDef?.alias || '',
          type: (fieldDef?.type === 'options' || fieldDef?.option_source === 'species_library' || (fieldDef?.options && fieldDef.options.length > 0)) ? 'options' : (fieldDef?.type || type || 'text'),
          options: fieldDef?.options || [],
          option_source: fieldDef?.option_source || 'static',
          species_field: fieldDef?.species_field || 'common_name',
          species_category: fieldDef?.species_category || 'All',
          sampleValue: sampleVal || fieldDef?.sample_data || '',
          fieldDef
        });
      };

      const traverse = (elements) => {
        (elements || []).forEach(el => {
          if (!el) return;
          if (el.type === 'text' || el.type === 'barcode' || el.type === 'qrcode') {
            if (el.dynamicFields && el.dynamicFields.length > 0) {
              el.dynamicFields.forEach(df => {
                if (df.type === 'dynamic' && df.label) {
                  add(df.datasetKey, df.label, 'text', df.sample_data);
                }
              });
            }
            if (el.text) {
              const matches = el.text.match(/\$[a-zA-Z0-9_]+\b/g);
              if (matches) {
                matches.forEach(m => {
                  add('', m.substring(1), 'text', '');
                });
              }
            }
          }
          if (el.type === 'image') {
            const varName = el.imageVariable || (el.imageType === 'frame' ? '$image_source' : '');
            if (varName && varName.startsWith('$')) {
              add('', varName.substring(1), 'image', el.src);
            } else if (el.dynamicFields && el.dynamicFields.length > 0) {
              el.dynamicFields.forEach(df => {
                if (df.label) add(df.datasetKey, df.label, 'image', df.sample_data || el.src);
              });
            }
          }
          if (el.type === 'datagrid' && Array.isArray(el.rows)) {
            el.rows.forEach(row => {
              if (row && row[1]) {
                const matches = String(row[1]).match(/\$[a-zA-Z0-9_]+\b/g);
                if (matches) {
                  matches.forEach(m => {
                    add('', m.substring(1), 'text', '');
                  });
                }
              }
            });
          }
        });
      };

      traverse(template.sides?.front?.elements);
      traverse(template.sides?.back?.elements);

      return list;
    };

    // Standard fallback mapping dictionary for known dynamic fields
    const DEFAULT_DYNAMIC_MAPPINGS = {
      'operator_name': 'org.name',
      'operator_web_address': 'org.website_address',
      'operator_email': 'org.enquiries_email',
      'operator_bookings_link': 'org.bookings_link',
      'trophy_country': 'org.billing_country',
      'country': 'org.billing_country',
      'Country': 'org.billing_country',
      'location': 'org.billing_country',
      'location_country': 'org.billing_country',
      'harvest_country': 'org.billing_country',
      'card_number_issued': 'coin_vault.coin_number',
      'log_date_year': 'system.current_year',
      'log_date_month': 'system.current_month_name',
      'logger_username': 'user.full_name'
    };

    // Helper to find dynamic data mapping key for a field
    const findMappingKey = (lbl, alias, fDefObj = null) => {
      if (fDefObj?.mappingKey) return fDefObj.mappingKey;
      if (fDefObj?.mapping) return fDefObj.mapping;
      if (fDefObj?.mappedTo) return fDefObj.mappedTo;
      if (fieldMappings && typeof fieldMappings === 'object') {
        if (lbl && fieldMappings[lbl]) return fieldMappings[lbl];
        if (alias && fieldMappings[alias]) return fieldMappings[alias];
        const norm = String(lbl || '').toLowerCase().replace(/[\s_-]+/g, '');
        for (const [k, v] of Object.entries(fieldMappings)) {
          if (k && k.toLowerCase().replace(/[\s_-]+/g, '') === norm && v) return v;
        }
        if (alias) {
          const normAlias = String(alias).toLowerCase().replace(/[\s_-]+/g, '');
          for (const [k, v] of Object.entries(fieldMappings)) {
            if (k && k.toLowerCase().replace(/[\s_-]+/g, '') === normAlias && v) return v;
          }
        }
      }
      if (lbl && DEFAULT_DYNAMIC_MAPPINGS[lbl]) return DEFAULT_DYNAMIC_MAPPINGS[lbl];
      if (alias && DEFAULT_DYNAMIC_MAPPINGS[alias]) return DEFAULT_DYNAMIC_MAPPINGS[alias];
      const normLbl = String(lbl || '').toLowerCase().replace(/[\s_-]+/g, '');
      if (DEFAULT_DYNAMIC_MAPPINGS[normLbl]) return DEFAULT_DYNAMIC_MAPPINGS[normLbl];
      return null;
    };

    const findDateFormat = (label, alias, fDef) => {
      const checkFormats = dateFormats || {};
      // 1. Direct match on label / alias / key
      if (label && checkFormats[label]) return checkFormats[label];
      if (alias && checkFormats[alias]) return checkFormats[alias];
      if (fDef?.label && checkFormats[fDef.label]) return checkFormats[fDef.label];
      if (fDef?.alias && checkFormats[fDef.alias]) return checkFormats[fDef.alias];
      if (fDef?.key && checkFormats[fDef.key]) return checkFormats[fDef.key];

      // 2. Normalized match (strip spaces, underscores, dashes, lowercase)
      const normLabel = String(label || '').toLowerCase().replace(/[\s_-]+/g, '');
      const normAlias = String(alias || '').toLowerCase().replace(/[\s_-]+/g, '');
      for (const [k, v] of Object.entries(checkFormats)) {
        if (!k || !v) continue;
        const normK = String(k).toLowerCase().replace(/[\s_-]+/g, '');
        if (normK === normLabel || (normAlias && normK === normAlias)) {
          return v;
        }
      }

      // 3. Check fDef date_format or validation
      if (fDef?.date_format) return fDef.date_format;
      if (fDef?.validation?.date_format) return fDef.validation.date_format;

      // Check for year-only or month-only field intents
      const normAll = `${normLabel} ${normAlias}`;
      if (normAll.includes('year') && !normAll.includes('day') && !normAll.includes('time')) {
        return 'YYYY';
      }
      if (normAll.includes('month') && !normAll.includes('day') && !normAll.includes('year') && !normAll.includes('time')) {
        return 'MMMM';
      }

      // 4. Default format if it's a date or time field so it NEVER outputs raw ISO 'T'
      const fType = String(fDef?.type || '').toLowerCase();
      const isDateOrTime = fType.includes('date') || fType.includes('time') ||
                           normLabel.includes('date') || normLabel.includes('time') ||
                           normAlias.includes('date') || normAlias.includes('time');
      if (isDateOrTime) {
        return getDefaultFormat(fType, label, alias);
      }

      return null;
    };

    // Helper to determine if a field is explicitly included in the template form vs omitted
    const isFieldIncluded = (field) => {
      const checkDict = (dict, key1, key2) => {
        if (!dict || typeof dict !== 'object') return undefined;
        if (key1 && dict[key1] !== undefined) return Boolean(dict[key1]);
        if (key2 && dict[key2] !== undefined) return Boolean(dict[key2]);
        const norm1 = String(key1 || '').toLowerCase().replace(/[\s_-]+/g, '');
        const norm2 = String(key2 || '').toLowerCase().replace(/[\s_-]+/g, '');
        for (const [k, v] of Object.entries(dict)) {
          if (typeof v === 'boolean') {
            const normK = String(k).toLowerCase().replace(/[\s_-]+/g, '');
            if (normK === norm1 || (norm2 && normK === norm2)) {
              return v;
            }
          }
        }
        return undefined;
      };

      const dsKey = field.datasetKey || '';
      let enabledForDS = null;
      if (dsKey) {
        const dsKeyStr = String(dsKey);
        const dsObj = currentDatasets[dsKeyStr] || currentDatasets[dsKey];
        const dsName = dsObj?.name;
        enabledForDS = enabledFields[dsKeyStr] || enabledFields[dsKey] || (dsName ? enabledFields[dsName] : null);
      }

      if (!enabledForDS && currentDatasets) {
        for (const [k, v] of Object.entries(enabledFields)) {
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            const dsObj = currentDatasets[k];
            if (dsObj && dsKey && (String(dsKey) === String(k) || (dsObj.name && dsObj.name === dsKey))) {
              enabledForDS = v;
              break;
            }
          }
        }
      }

      const dsVal = enabledForDS ? checkDict(enabledForDS, field.label, field.alias) : undefined;
      const flatVal = checkDict(enabledFields, field.label, field.alias);

      if (dsVal === false || (dsVal === undefined && flatVal === false)) {
        return false; // Explicitly omitted by template author
      }

      return true;
    };

    // Helper to determine if a field is hidden from wizard input UI (but still captured)
    const isFieldHidden = (field) => {
      const checkDict = (dict, key1, key2) => {
        if (!dict || typeof dict !== 'object') return undefined;
        if (key1 && dict[key1] !== undefined) {
          return dict[key1] === true || dict[key1] === 'hidden';
        }
        if (key2 && dict[key2] !== undefined) {
          return dict[key2] === true || dict[key2] === 'hidden';
        }
        const norm1 = String(key1 || '').toLowerCase().replace(/[\s_-]+/g, '');
        const norm2 = String(key2 || '').toLowerCase().replace(/[\s_-]+/g, '');
        for (const [k, v] of Object.entries(dict)) {
          const normK = String(k).toLowerCase().replace(/[\s_-]+/g, '');
          if (normK === norm1 || (norm2 && normK === norm2)) {
            return v === true || v === 'hidden';
          }
        }
        return undefined;
      };

      const dsKey = field.datasetKey || '';
      let hiddenForDS = null;
      let enabledForDS = null;
      if (dsKey) {
        const dsKeyStr = String(dsKey);
        const dsObj = currentDatasets[dsKeyStr] || currentDatasets[dsKey];
        const dsName = dsObj?.name;
        hiddenForDS = hiddenFields[dsKeyStr] || hiddenFields[dsKey] || (dsName ? hiddenFields[dsName] : null);
        enabledForDS = enabledFields[dsKeyStr] || enabledFields[dsKey] || (dsName ? enabledFields[dsName] : null);
      }

      if (!hiddenForDS && currentDatasets) {
        for (const [k, v] of Object.entries(hiddenFields)) {
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            const dsObj = currentDatasets[k];
            if (dsObj && dsKey && (String(dsKey) === String(k) || (dsObj.name && dsObj.name === dsKey))) {
              hiddenForDS = v;
              break;
            }
          }
        }
      }

      if (!enabledForDS && currentDatasets) {
        for (const [k, v] of Object.entries(enabledFields)) {
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            const dsObj = currentDatasets[k];
            if (dsObj && dsKey && (String(dsKey) === String(k) || (dsObj.name && dsObj.name === dsKey))) {
              enabledForDS = v;
              break;
            }
          }
        }
      }

      const dsHidden = hiddenForDS ? checkDict(hiddenForDS, field.label, field.alias) : undefined;
      const flatHidden = checkDict(hiddenFields, field.label, field.alias);
      const dsEnabledIsHidden = enabledForDS ? (enabledForDS[field.label] === 'hidden' || (field.alias && enabledForDS[field.alias] === 'hidden')) : false;
      const flatEnabledIsHidden = enabledFields[field.label] === 'hidden' || (field.alias && enabledFields[field.alias] === 'hidden');

      if (dsHidden === true || (dsHidden === undefined && flatHidden === true) || dsEnabledIsHidden || flatEnabledIsHidden) {
        return true;
      }
      return false;
    };

    // 2. Build grouped fields mapping strictly from dynamic fields defined on the template
    const dynamicFields = getDynamicFieldsInTemplate();
    const groups = {};
    const allCapturedFields = [];

    dynamicFields.forEach(field => {
      if (!isFieldIncluded(field)) {
        return; // Omitted from form wizard (no data captured)
      }

      const isHidden = isFieldHidden(field);

      const dsId = field.datasetKey || '';
      let dsName = 'General Variables';
      if (dsId && currentDatasets[dsId]) {
        dsName = currentDatasets[dsId].name || dsId;
      } else {
        for (const k of Object.keys(currentDatasets)) {
          if (currentDatasets[k]?.fields?.some(f => f.label === field.label)) {
            dsName = currentDatasets[k].name || k;
            break;
          }
        }
      }

      const fDef = field.fieldDef;
      const mappingKey = findMappingKey(field.label, field.alias, fDef);
      const isExplicitlyLocked = (lockedOptions[field.label] !== undefined && lockedOptions[field.label] !== '') ||
                                (field.alias && lockedOptions[field.alias] !== undefined && lockedOptions[field.alias] !== '') ||
                                Boolean(fDef?.isLocked || fDef?.is_locked || fDef?.readonly || fDef?.readOnly);
      const isMapped = Boolean(mappingKey);
      const isLocked = Boolean(isExplicitlyLocked || isMapped);
      const fieldDateFormat = findDateFormat(field.label, field.alias, fDef);

      const fieldObj = {
        key: field.label,
        label: field.alias || field.label.replace(/_/g, ' '),
        description: fDef?.description || '',
        datasetKey: String(dsId),
        datasetName: dsName,
        type: field.type || (fDef?.type === 'options' || fDef?.option_source === 'species_library' || (fDef?.options && fDef.options.length > 0) ? 'options' : (fDef?.type || 'text')),
        options: field.options || fDef?.options || [],
        option_source: field.option_source || fDef?.option_source || 'static',
        species_field: field.species_field || fDef?.species_field || 'common_name',
        species_category: field.species_category || fDef?.species_category || 'All',
        required: isHidden ? false : (requiredFields[field.label] !== undefined ? !!requiredFields[field.label] : !!fDef?.required),
        isHidden: isHidden,
        isLocked: isLocked,
        isMapped: isMapped,
        isReadOnly: isLocked,
        lockedValue: lockedOptions[field.label] !== undefined ? lockedOptions[field.label] : null,
        mappingKey: mappingKey,
        dateFormat: fieldDateFormat,
        sampleValue: fDef?.sample_data || field.sampleValue || '',
        defaultValue: '',
        validation: fDef?.validation || null,
        render: fDef?.render || null,
        custom_renderer: fDef?.custom_renderer || null
      };

      allCapturedFields.push(fieldObj);

      if (!isHidden) {
        if (!groups[dsName]) groups[dsName] = [];
        groups[dsName].push(fieldObj);
      }
    });

    // 3. Fallback if template has no linked datasets or elements with variables
    if (Object.keys(groups).length === 0 && dynamicFields.length === 0) {
      const fallbackFields = [
        { key: 'trophy_owner_name', label: 'Angler Name', category: 'General Information', type: 'text', defaultValue: '', sampleValue: '' },
        { key: 'fish_type', label: 'Species', category: 'General Information', type: 'text', defaultValue: '', sampleValue: '' },
        { key: 'fish_weight', label: 'Weight', category: 'Award Details', type: 'text', defaultValue: '', sampleValue: '' },
        { key: 'catch_date', label: 'Date Awarded', category: 'Award Details', type: 'date', defaultValue: '', sampleValue: '' }
      ];
      groups['General Information'] = fallbackFields;
      allCapturedFields.push(...fallbackFields);
    }

    // 4. Order the dataset pages (matching TemplateDesigner Form tab order)
    const existingGroupNames = Object.keys(groups);
    const orderList = Array.isArray(formDatasetOrder) ? formDatasetOrder : [];
    const linkedGroupNames = linkedDatasets
      .map(id => currentDatasets[String(id)]?.name)
      .filter(name => name && existingGroupNames.includes(name));

    const orderedGroupNames = orderList.length > 0
      ? [
          ...orderList.filter(name => existingGroupNames.includes(name)),
          ...existingGroupNames.filter(name => !orderList.includes(name))
        ]
      : [
          ...linkedGroupNames,
          ...existingGroupNames.filter(name => !linkedGroupNames.includes(name))
        ];

    const pages = orderedGroupNames.map(groupName => ({
      title: groupName,
      fields: groups[groupName] || []
    })).filter(page => page.fields.length > 0);

    setWizardPages(pages);

    // 5. Initial form values
    const effectiveUser = passedUser || operatorUser;
    const effectiveOrg = passedOrg || orgData || coin?.organization;

    const resolveClientMapping = (mKey) => {
      if (!mKey) return '';
      switch (mKey) {
        case 'org.name': return effectiveOrg?.name || coin?.organization?.name || coin?.tenant?.brand_name || '';
        case 'org.website_address': return effectiveOrg?.website_address || coin?.organization?.website_address || (coin?.tenant?.domain_name ? `https://${coin.tenant.domain_name}` : '');
        case 'org.enquiries_email': return effectiveOrg?.enquiries_email || coin?.organization?.enquiries_email || effectiveUser?.email || '';
        case 'org.bookings_link': return effectiveOrg?.bookings_link || coin?.organization?.bookings_link || '';
        case 'org.social_media': return effectiveOrg?.social_media || coin?.organization?.social_media || '';
        case 'org.logo_url': return effectiveOrg?.logo_url || coin?.organization?.logo_url || coin?.tenant?.logo_url || '';
        case 'org.billing_address': return effectiveOrg?.billing_address || coin?.organization?.billing_address || '';
        case 'org.billing_city': return effectiveOrg?.billing_city || coin?.organization?.billing_city || '';
        case 'org.billing_state': return effectiveOrg?.billing_state || coin?.organization?.billing_state || '';
        case 'org.billing_zip': return effectiveOrg?.billing_zip || coin?.organization?.billing_zip || '';
        case 'org.billing_country': return effectiveOrg?.billing_country || coin?.organization?.billing_country || effectiveUser?.billing_country || '';
        case 'user.full_name': return effectiveUser?.name || effectiveUser?.email || '';
        case 'user.first_name': return effectiveUser?.name?.split(' ')[0] || '';
        case 'user.last_name': return effectiveUser?.name?.split(' ').slice(1).join(' ') || '';
        case 'user.email': return effectiveUser?.email || '';
        case 'user.phone_number': return effectiveUser?.phone_number || '';
        case 'user.company_name': return effectiveOrg?.name || coin?.organization?.name || coin?.tenant?.brand_name || '';
        case 'user.role': return effectiveUser?.role || 'Operator';
        case 'operator_name':
        case 'operator.name':
          return effectiveOrg?.name || coin?.organization?.name || coin?.tenant?.brand_name || '';
        case 'operator_web_address':
        case 'operator.website_address':
          return effectiveOrg?.website_address || coin?.organization?.website_address || (coin?.tenant?.domain_name ? `https://${coin.tenant.domain_name}` : '');
        case 'operator_email':
        case 'operator.email':
          return effectiveOrg?.enquiries_email || coin?.organization?.enquiries_email || effectiveUser?.email || '';
        case 'operator_bookings_link':
        case 'operator.bookings_link':
          return effectiveOrg?.bookings_link || coin?.organization?.bookings_link || '';
        case 'trophy_country':
        case 'country':
        case 'Country':
        case 'location':
        case 'location_country':
        case 'harvest_country':
          return effectiveOrg?.billing_country || coin?.organization?.billing_country || effectiveUser?.billing_country || '';
        case 'card_number_issued':
          return coin?.card_data?.card_number_issued || (coin?.coin_number !== undefined ? String(coin.coin_number) : '');
        case 'log_date_year':
          return String(new Date().getFullYear());
        case 'log_date_month':
          return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date());
        case 'logger_username':
          return effectiveUser?.name || effectiveUser?.email || 'Operator';
        case 'tenant.brand_name': return coin?.tenant?.brand_name || '';
        case 'tenant.domain_name': return coin?.tenant?.domain_name || '';
        case 'tenant.logo_url': return coin?.tenant?.logo_url || '';
        case 'coin_vault.coin_number':
        case 'coin.coin_number':
          return coin?.coin_number !== undefined ? String(coin.coin_number) : '';
        case 'coin_vault.total_coins':
        case 'coin.total_coins':
          return coin?.total_coins !== undefined ? String(coin.total_coins) : '';
        case 'coin_vault.coin_fraction':
        case 'coin.coin_fraction':
          return coin?.coin_number && coin?.total_coins ? `#${coin.coin_number} of ${coin.total_coins}` : (coin?.coin_number ? `#${coin.coin_number}` : '');
        case 'coin_vault.public_code':
        case 'coin.public_code':
          return coin?.public_code || '';
        case 'coin_vault.rfid_url':
        case 'coin.rfid_url':
          return coin?.rfid_url || '';
        case 'coin_vault.minted_at':
        case 'coin.minted_at':
          return coin?.minted_at ? String(coin.minted_at).split('T')[0] : '';
        case 'coin_vault.order_id':
        case 'coin.order_id':
          return coin?.order_id !== undefined ? String(coin.order_id) : '';
        case 'coin_vault.checksum_front':
        case 'coin.checksum_front':
          return coin?.md5_front || '';
        case 'coin_vault.checksum_back':
        case 'coin.checksum_back':
          return coin?.md5_back || '';
        case 'coin_vault.preview_front_url':
        case 'coin.preview_front_url':
          return coin?.preview_front_url || '';
        case 'coin_vault.preview_back_url':
        case 'coin.preview_back_url':
          return coin?.preview_back_url || '';
        case 'system.current_year': return String(new Date().getFullYear());
        case 'system.current_date': return new Date().toISOString().split('T')[0];
        case 'system.current_month_name': return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date());
        case 'system.current_month_num': return String(new Date().getMonth() + 1).padStart(2, '0');
        case 'system.current_day': return String(new Date().getDate()).padStart(2, '0');
        case 'system.current_day_name': return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date());
        case 'system.current_time': return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        case 'system.current_datetime': return `${new Date().toISOString().split('T')[0]} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        default: return '';
      }
    };

    const initialData = {};
    allCapturedFields.forEach(field => {
      let val = '';
      if (field.isLocked && field.lockedValue !== null && field.lockedValue !== undefined && field.lockedValue !== '') {
        val = field.lockedValue;
      } else {
        if (field.mappingKey && prefilled[field.mappingKey] !== undefined && prefilled[field.mappingKey] !== '') {
          val = prefilled[field.mappingKey];
        } else if (prefilled[field.key] !== undefined && prefilled[field.key] !== '') {
          val = prefilled[field.key];
        } else if (field.mappingKey) {
          const clientVal = resolveClientMapping(field.mappingKey);
          if (clientVal) val = clientVal;
        } else {
          const directClientVal = resolveClientMapping(field.key);
          if (directClientVal) val = directClientVal;
        }
      }
      if (!val || String(val).trim() === '') {
        val = field.defaultValue || '';
      }
      if (field.dateFormat && val) {
        val = formatDateTimeValue(val, field.dateFormat);
      }
      initialData[field.key] = val;
    });

    // Helper to determine if an entire dataset page is pre-populated and valid
    const isPageComplete = (page, currentData) => {
      if (!page || !page.fields || page.fields.length === 0) return false;
      for (const f of page.fields) {
        const fVal = currentData[f.key];
        const hasFVal = fVal !== undefined && fVal !== null && String(fVal).trim() !== '';
        if (!hasFVal) return false;
        if (isCoordinateField(f) && String(fVal).trim().length < 3) return false;
      }
      return true;
    };

    setFormData(prev => {
      const next = { ...initialData };
      for (const [k, v] of Object.entries(prev)) {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          next[k] = v;
        }
      }

      // Preserve prefilled or coin-stored EXIF and original media metadata
      if (prefilled && typeof prefilled === 'object') {
        for (const [k, v] of Object.entries(prefilled)) {
          if (k.endsWith('_exif') || k.endsWith('_original')) {
            next[k] = v;
          }
        }
      }
      if (coin?.card_data && typeof coin.card_data === 'object') {
        for (const [k, v] of Object.entries(coin.card_data)) {
          if (k.endsWith('_exif') || k.endsWith('_original')) {
            next[k] = v;
          }
        }
      }

      // Sync image fields with prefilled EXIF data into imageExifMap
      allCapturedFields.forEach(f => {
        if (f.type === 'image') {
          const exifVal = next[`${f.key}_exif`] || prefilled?.[`${f.key}_exif`] || coin?.card_data?.[`${f.key}_exif`];
          if (exifVal) {
            setImageExifMap(prevMap => ({ ...prevMap, [f.key]: exifVal }));
          }
        }
      });

      const templateCfg = template.config || template || {};
      const hidePrepopulated = Boolean(
        templateCfg.hide_prepopulated_datasets ??
        templateCfg.hidePrepopulatedDatasets ??
        templateCfg.skip_prepopulated_datasets ??
        false
      );

      if (hidePrepopulated && pages.length > 0) {
        let startIdx = 0;
        while (startIdx < pages.length && isPageComplete(pages[startIdx], next)) {
          startIdx++;
        }
        if (startIdx < pages.length && startIdx > 0) {
          setCurrentPageIndex(prevIdx => (prevIdx === 0 ? startIdx : prevIdx));
        }
      }

      return next;
    });
  };

  // Helper to determine if an entire dataset page is pre-populated and valid (for runtime wizard navigation)
  const isPagePrepopulatedAndValid = useCallback((page, currentFormData = formData) => {
    if (!page || !page.fields || page.fields.length === 0) return false;
    for (const f of page.fields) {
      const val = currentFormData[f.key];
      const hasVal = val !== undefined && val !== null && String(val).trim() !== '';
      if (!hasVal) return false;
      if (isCoordinateField(f) && String(val).trim().length < 3) return false;
    }
    return true;
  }, [formData]);

  // Helper to identify wizard fields mapped to EXIF attributes (Date, Time, Location, Place)
  const getExifMappingTargets = useCallback(() => {
    const targets = {
      date: null,
      time: null,
      location: null,
      place: null
    };
    const allFields = wizardPages.flatMap(p => p.fields || []);
    allFields.forEach(f => {
      const m = f.mappingKey || '';
      const pickerType = getPickerTypeForFormat(f.type, f.dateFormat, f.label, f.alias);
      const isTimeField = f.type === 'time' || pickerType === 'time' || /(?:^|_|\b)time(?:$|_|\b)/i.test(`${f.key} ${f.label} ${f.alias || ''}`);
      const isDateField = f.type === 'date' || pickerType === 'date' || /(?:^|_|\b)date(?:$|_|\b)/i.test(`${f.key} ${f.label} ${f.alias || ''}`);

      if (m === 'photo_exif.time' || (m === 'photo_exif.date_taken' && isTimeField && !isDateField)) {
        if (!targets.time) targets.time = f;
      } else if (m === 'photo_exif.date' || (m === 'photo_exif.date_taken' && isDateField && !isTimeField)) {
        if (!targets.date) targets.date = f;
      } else if (m === 'photo_exif.date_taken') {
        if (!targets.date) targets.date = f;
        else if (!targets.time && isTimeField) targets.time = f;
      }

      if (m === 'photo_exif.gps_coordinates' || m === 'photo_exif.gps_latitude' || m === 'photo_exif.gps_longitude' || m === 'photo_exif.gps_dms') {
        if (!targets.location) targets.location = f;
      }
      if (m === 'photo_exif.place' || m === 'photo_exif.gps_altitude' || m === 'country' || m === 'harvest_country' || m === 'location_place' || m === 'location') {
        if (!targets.place) targets.place = f;
      }
    });
    return targets;
  }, [wizardPages]);

  // --- Coordinate Handlers (GPS & Interactive Map) ---
  const handleAcquireDeviceLocation = (field) => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your mobile browser');
      return;
    }

    setLocatingFieldKey(field.key);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const formatted = formatCoordinates(lat, lng, field.validation?.format || 'dd');
        setFormData(prev => ({ ...prev, [field.key]: formatted }));
        setLocatingFieldKey(null);
        if (validationError) setValidationError('');
      },
      (error) => {
        setLocatingFieldKey(null);
        let msg = 'Could not acquire location.';
        if (error.code === 1) msg = 'Location permission denied. Please allow location access or select on map.';
        else if (error.code === 2) msg = 'Location unavailable. Please select on map.';
        else if (error.code === 3) msg = 'Location request timed out. Please try again or select on map.';
        alert(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleOpenMapPicker = (field) => {
    setActiveMapField(field);
  };

  const handleSelectMapCoordinates = (formattedValue) => {
    if (activeMapField) {
      setFormData(prev => ({ ...prev, [activeMapField.key]: formattedValue }));
      if (validationError) setValidationError('');
    }
    setActiveMapField(null);
  };

  // --- Wizard Navigation & Validation ---
  const handleNextStep = () => {
    const page = wizardPages[currentPageIndex];
    if (!page) return;

    // Validate required fields on current page
    // Note: If a mapped field (e.g. photo EXIF GPS or org) has no data, it reverts to manual input.
    // Therefore, if required, the user must provide a value before proceeding.
    const missing = [];
    (page.fields || []).forEach(f => {
      const rawVal = formData[f.key];
      const hasVal = rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '';
      const isExifM = Boolean(f.mappingKey && f.mappingKey.startsWith('photo_exif.'));
      const isExpLock = Boolean((f.lockedValue !== null && f.lockedValue !== undefined && f.lockedValue !== '') || f.isExplicitlyLocked);
      const isFldReadOnly = isExpLock || (Boolean(f.mappingKey || f.isMapped) && !isExifM && hasVal);

      if (f.required && !isFldReadOnly) {
        if (!hasVal) {
          missing.push(f.label);
        }
      }
    });

    if (missing.length > 0) {
      setValidationError(`Please complete required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`);
      return;
    }

    setValidationError('');

    const templateCfg = coin?.card_template_snapshot?.config || coin?.card_template_snapshot || {};
    const hidePrepopulatedDatasets = Boolean(
      templateCfg.hide_prepopulated_datasets ??
      templateCfg.hidePrepopulatedDatasets ??
      templateCfg.skip_prepopulated_datasets ??
      false
    );

    let nextIndex = currentPageIndex + 1;
    if (hidePrepopulatedDatasets) {
      while (nextIndex < wizardPages.length && isPagePrepopulatedAndValid(wizardPages[nextIndex], formData)) {
        nextIndex++;
      }
    }
    setCurrentPageIndex(nextIndex);
  };

  const handleBackStep = () => {
    setValidationError('');
    setCurrentPageIndex(prev => Math.max(0, prev - 1));
  };

  // --- Finalize & Issue ---
  const handleFinalizeAndIssue = async () => {
    setIssuingLoading(true);
    setIssuingError('');
    try {
      const frontEl = frontCardRef.current;
      const backEl = backCardRef.current;

      const frontHtml = frontEl?.firstElementChild ? frontEl.firstElementChild.outerHTML : (frontEl ? frontEl.outerHTML : '');
      let backHtml = backEl?.firstElementChild ? backEl.firstElementChild.outerHTML : (backEl ? backEl.outerHTML : '');

      // Normalize back HTML so Chrome screenshot doesn't render it rotated or hidden
      if (backHtml) {
        backHtml = backHtml
          .replace(/transform:\s*rotateY\([^)]+\);?/gi, 'transform: none;')
          .replace(/backface-visibility:\s*hidden;?/gi, '');
      }

      // Helper to strip 3D rotation, perspective, and fix calc() in cloned DOM before snapshotting
      const prepareCardForSnapshot = (clonedDoc, targetId, sideAttr) => {
        // 1. Strip all 3D rotations, perspective, and matrix3d across cloned document
        const allNodes = clonedDoc.querySelectorAll('*');
        allNodes.forEach(node => {
          if (node.style) {
            if (node.style.transform && (node.style.transform.includes('rotateY') || node.style.transform.includes('matrix3d'))) {
              node.style.transform = 'none';
              node.style.webkitTransform = 'none';
            }
            if (node.style.perspective) {
              node.style.perspective = 'none';
              node.style.webkitPerspective = 'none';
            }
            if (node.style.transformStyle) {
              node.style.transformStyle = 'flat';
            }
            if (node.style.backfaceVisibility) {
              node.style.backfaceVisibility = 'visible';
              node.style.webkitBackfaceVisibility = 'visible';
            }
          }
        });

        // 2. Identify the target element and neutralize all its ancestors
        const el = clonedDoc.getElementById(targetId) || clonedDoc.querySelector(`[data-card-side="${sideAttr}"]`);
        if (el) {
          el.style.transform = 'none';
          el.style.webkitTransform = 'none';
          el.style.backfaceVisibility = 'visible';
          el.style.webkitBackfaceVisibility = 'visible';

          let curr = el.parentElement;
          while (curr && curr !== clonedDoc.body && curr !== clonedDoc) {
            if (curr.style) {
              curr.style.transform = 'none';
              curr.style.webkitTransform = 'none';
              curr.style.perspective = 'none';
              curr.style.transformStyle = 'flat';
              curr.style.backfaceVisibility = 'visible';
              curr.style.webkitBackfaceVisibility = 'visible';
            }
            curr = curr.parentElement;
          }

          // 3. Fallback conversion for any calc() on img elements for html2canvas compatibility
          const imgs = el.querySelectorAll('img');
          imgs.forEach(img => {
            ['left', 'top'].forEach(prop => {
              const val = img.style[prop];
              if (val && val.includes('calc')) {
                const m = val.match(/calc\(50%\s*([+-])\s*([\d.]+)%\)/);
                if (m) {
                  const sign = m[1] === '-' ? -1 : 1;
                  const delta = parseFloat(m[2]) || 0;
                  img.style[prop] = `${50 + (sign * delta)}%`;
                }
              }
            });
          });
        }
        return el;
      };

      // Capture client-side previews using html2canvas for instant, reliable snapshot rendering
      let frontBase64 = null;
      let backBase64 = null;
      try {
        const targetFront = document.getElementById('coin-card-side-front') || frontEl;
        const targetBack = document.getElementById('coin-card-side-back') || backEl;

        if (targetFront) {
          const canvasFront = await html2canvas(targetFront, {
            backgroundColor: null,
            useCORS: true,
            allowTaint: true,
            scale: 2.0,
            logging: false,
            onclone: (clonedDoc) => {
              prepareCardForSnapshot(clonedDoc, 'coin-card-side-front', 'front');
            }
          });
          const dataUrl = canvasFront.toDataURL('image/png');
          if (dataUrl && dataUrl.length > 500) {
            frontBase64 = dataUrl;
          }
        }

        if (targetBack) {
          const canvasBack = await html2canvas(targetBack, {
            backgroundColor: null,
            useCORS: true,
            allowTaint: true,
            scale: 2.0,
            logging: false,
            onclone: (clonedDoc) => {
              prepareCardForSnapshot(clonedDoc, 'coin-card-side-back', 'back');
            }
          });
          const dataUrl = canvasBack.toDataURL('image/png');
          if (dataUrl && dataUrl.length > 500) {
            backBase64 = dataUrl;
          }
        }
      } catch (snapErr) {
        console.warn('html2canvas card preview capture error:', snapErr);
      }

      const template = coin?.card_template_snapshot || {};
      const width = template.cardDimensions?.width || 350;
      const height = template.cardDimensions?.height || 490;

      const res = await fetch(`${API_BASE}/api/v1/coin-vault/${public_code}/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${operatorToken}`
        },
        body: JSON.stringify({
          card_data: formData,
          front_html: frontHtml,
          back_html: backHtml,
          front_image: frontBase64,
          back_image: backBase64,
          width,
          height,
          card_template_snapshot: coin?.card_template_snapshot
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to issue coin');
      }

      setShowConfirmModal(false);
      // Refresh coin to show customer issued view
      await fetchCoin();
    } catch (err) {
      setIssuingError(err.message || 'Issuance failed. Please check your network and try again.');
    } finally {
      setIssuingLoading(false);
    }
  };

  // --- Verify Authenticity ---
  const handleVerify = async () => {
    setVerifying(true);
    setVerifyError('');
    if (stampTimeoutId) clearTimeout(stampTimeoutId);

    try {
      const res = await fetch(`${API_BASE}/api/v1/coin-vault/${public_code}/verify`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Verification error');
      }

      if (data.verified) {
        setShowStamp(true);
        const tid = setTimeout(() => {
          setShowStamp(false);
        }, 10000);
        setStampTimeoutId(tid);
      } else {
        setVerifyError('Data mismatch, card cant be verified at this time.');
      }
    } catch (err) {
      setVerifyError(err.message || 'data mismatch, card cant be verified at this time');
    } finally {
      setVerifying(false);
    }
  };

  // --- Main Render ---
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0d14', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', color: '#38bdf8' }}>
            <NeutralIcons.Spinner size={40} />
          </div>
          <p style={{ marginTop: '12px', fontSize: '14px', color: '#94a3b8' }}>Reading CoinVault Record...</p>
        </div>
      </div>
    );
  }

  if (error || !coin) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 30%, #152238 0%, #080c14 100%)',
        color: '#fff',
        padding: '24px',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif"
      }}>
        <div style={{
          maxWidth: '440px',
          width: '100%',
          textAlign: 'center',
          background: 'rgba(18, 26, 42, 0.88)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(212, 175, 55, 0.35)',
          borderRadius: '20px',
          padding: '40px 28px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6), 0 0 35px rgba(212, 175, 55, 0.08)'
        }}>
          <div style={{
            width: '68px',
            height: '68px',
            margin: '0 auto 20px auto',
            borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 40%, rgba(212, 175, 55, 0.25) 0%, rgba(20, 30, 48, 0.6) 100%)',
            border: '1px solid rgba(212, 175, 55, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#d4af37'
          }}>
            <NeutralIcons.Alert size={34} />
          </div>
          <div style={{
            display: 'inline-block',
            background: 'rgba(212, 175, 55, 0.12)',
            color: '#d4af37',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            borderRadius: '999px',
            padding: '4px 14px',
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            marginBottom: '14px'
          }}>
            Verification Notice
          </div>
          <h2 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '800', color: '#ffffff' }}>
            Trophy Card Not Found
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8', lineHeight: 1.6 }}>
            We couldn't locate a verified Trophy Card matching this link. The code may be mistyped, expired, or the coin has not yet been registered.
          </p>
          <div style={{ marginTop: '24px', fontSize: '11px', fontWeight: '600', color: '#64748b', letterSpacing: '0.5px' }}>
            AUTHENTICITY &bull; VERIFICATION &bull; SECURITY
          </div>
        </div>
      </div>
    );
  }


  const template = coin.card_template_snapshot || {};
  const dims = template.cardDimensions || { width: 350, height: 490 };
  const hasVerifyObject = Boolean(hasCardVerifyObject(template));

  // =========================================================================
  // CASE B: ISSUED VIEW (Customer 3D Flip Card View + Share + Operator Connect)
  // =========================================================================
  if (coin.is_issued) {
    const cleanCardUrl = `${window.location.origin}/c/${coin.public_code}`;
    const getPlatformShareUrl = (platform) => `${cleanCardUrl}?src=${platform}`;
    const cardTitle = coin?.card_data?.['species'] || coin?.card_data?.['fish_type'] || template?.name || 'Trophy Card';
    const payoffLine = (coin?.tenant?.share_payoff_line || tenantConfig?.share_payoff_line || '').trim() || 'Official Certified Trophy Card';
    const rawTenantIcon = coin?.tenant?.site_icon_url || coin?.tenant?.logo_url || tenantConfig?.site_icon_url || tenantConfig?.logo_url || '';
    const tenantIconUrl = rawTenantIcon && rawTenantIcon.startsWith('/uploads/') && API_BASE
      ? `${API_BASE.replace(/\/+$/, '')}${rawTenantIcon}`
      : (rawTenantIcon || '/favicon.svg');
    const tenantBrandName = coin?.tenant?.brand_name || coin?.organization?.name || tenantConfig?.brand_name || 'TrophyCoin';
    const shareText = `Check out this official ${cardTitle} Trophy Card! 🏆`;

    const handleCopyShareLink = async () => {
      trackEvent('share', { source: 'copy', label: 'Card Link Copied' });
      const success = await safeCopyToClipboard(getPlatformShareUrl('copy'));
      if (success) {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      }
    };

    const handleFacebookShare = async (e) => {
      if (e) e.preventDefault();
      trackEvent('share', { source: 'facebook', label: 'Card Shared to Facebook' });

      const shareUrl = getPlatformShareUrl('facebook');
      const fullShareText = `${shareText}\n${shareUrl}`;

      // Automatically copy message & card link to clipboard for convenience
      await safeCopyToClipboard(fullShareText);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);

      // On mobile devices, invoke native OS share sheet (shares directly into native Facebook app)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
      if (isMobile && typeof navigator !== 'undefined' && navigator.share && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: `Trophy Card: ${cardTitle}`,
            text: shareText,
            url: shareUrl
          });
          return;
        } catch (err) {
          if (err.name === 'AbortError') return;
        }
      }

      // Desktop: Open centered Facebook share popup dialog with display=popup & quote
      const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}&display=popup`;
      const width = 640;
      const height = 580;
      const left = Math.max(0, Math.round((window.innerWidth - width) / 2 + (window.screenX || 0)));
      const top = Math.max(0, Math.round((window.innerHeight - height) / 2 + (window.screenY || 0)));
      const popup = window.open(
        fbUrl,
        'facebook-share-dialog',
        `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes,location=yes,toolbar=no,menubar=no`
      );
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        window.open(fbUrl, '_blank');
      }
    };

    const handleNativeShare = async () => {
      trackEvent('share', { source: 'native', label: 'Phone Share Sheet Opened' });
      if (typeof navigator !== 'undefined' && navigator.share && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: `Trophy Card: ${cardTitle}`,
            text: shareText,
            url: getPlatformShareUrl('native')
          });
          return;
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.warn('[Share] navigator.share error:', err);
          }
        }
      }
    };

    const handleLinkClick = (linkType, linkInfo) => {
      const destUrl = (typeof linkInfo === 'object' && linkInfo !== null) ? (linkInfo.url || null) : (typeof linkInfo === 'string' ? linkInfo : null);
      if (linkType === 'website') {
        trackEvent('click_website', { source: 'card_action', label: 'Operator Website Clicked', destination_url: destUrl });
      } else if (linkType === 'booking') {
        trackEvent('click_booking', { source: 'card_action', label: 'Booking Link Clicked', destination_url: destUrl });
      } else if (linkType === 'email') {
        trackEvent('click_email', { source: 'card_action', label: 'Email Inquiry Clicked', destination_url: destUrl });
      }
    };

    return (
      <div style={{
        minHeight: '100dvh',
        background: 'radial-gradient(circle at 50% 50%, #172033 0%, #080c14 100%)',
        color: '#fff',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 15px',
        boxSizing: 'border-box'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 'auto 0'
        }}>
          {/* Subtle notice when operator subscription is expired */}
          {coin?.is_subscription_expired && (
            <div style={{
              fontSize: '11px',
              color: '#94a3b8',
              marginBottom: '10px',
              textAlign: 'center',
              letterSpacing: '0.4px',
              fontWeight: 500,
              userSelect: 'none'
            }}>
              operator subscription expired
            </div>
          )}

          {/* 3D Flippable Card with Proportional Scaling */}
          <ResponsiveFlippableCard
            template={template}
            cardData={coin.card_data}
            isFlipped={isFlipped}
            onFlip={() => setIsFlipped(!isFlipped)}
            frontCardRef={frontCardRef}
            backCardRef={backCardRef}
            datasets={datasets}
            showStamp={showStamp}
            user={coin?.issued_by}
            orgData={coin?.organization}
            tenantConfig={coin?.tenant}
            onVerify={handleVerify}
            verifying={verifying}
            onLinkClick={handleLinkClick}
            isSubscriptionExpired={Boolean(coin?.is_subscription_expired)}
          />

          {/* Verification Alert Banner */}
          {verifyError && (
            <div style={{ maxWidth: `${dims.width}px`, width: '100%', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.35)', color: '#fca5a5', padding: '10px 14px', borderRadius: '8px', fontSize: '12px', textAlign: 'center', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <NeutralIcons.Alert size={14} />
              <span>{verifyError}</span>
            </div>
          )}

          {/* In-flight Verification Indicator */}
          {verifying && hasVerifyObject && (
            <div style={{
              position: 'relative',
              margin: '12px 0 6px 0',
              padding: '9px 18px',
              borderRadius: '24px',
              background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.22) 0%, rgba(14, 165, 233, 0.12) 100%)',
              border: '1px solid rgba(56, 189, 248, 0.55)',
              color: '#38bdf8',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              overflow: 'hidden',
              boxShadow: '0 0 16px rgba(56, 189, 248, 0.25), inset 0 0 12px rgba(2, 132, 199, 0.15)',
              animation: 'trophy-verify-pulse 1.8s ease-in-out infinite',
              userSelect: 'none'
            }}>
              <style>{`
                @keyframes trophy-verify-spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                @keyframes trophy-verify-shimmer {
                  0% { transform: translateX(-150%) skewX(-20deg); }
                  100% { transform: translateX(350%) skewX(-20deg); }
                }
                @keyframes trophy-verify-pulse {
                  0%, 100% {
                    border-color: rgba(56, 189, 248, 0.45);
                    box-shadow: 0 0 10px rgba(56, 189, 248, 0.2), inset 0 0 10px rgba(2, 132, 199, 0.1);
                  }
                  50% {
                    border-color: rgba(56, 189, 248, 0.85);
                    box-shadow: 0 0 20px rgba(56, 189, 248, 0.45), inset 0 0 15px rgba(2, 132, 199, 0.25);
                  }
                }
                @keyframes trophy-dot-blink {
                  0%, 20% { opacity: 0.2; transform: translateY(0); }
                  50% { opacity: 1; transform: translateY(-1.5px); }
                  80%, 100% { opacity: 0.2; transform: translateY(0); }
                }
                @keyframes trophy-radar-ping {
                  0% { transform: scale(0.9); opacity: 0.9; }
                  70%, 100% { transform: scale(2.4); opacity: 0; }
                }
              `}</style>

              {/* Sweeping radar/scanner beam across the pill */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                overflow: 'hidden',
                borderRadius: 'inherit'
              }}>
                <div style={{
                  width: '40%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(56, 189, 248, 0.28) 50%, transparent 100%)',
                  animation: 'trophy-verify-shimmer 1.8s ease-in-out infinite'
                }} />
              </div>

              {/* High-tech spinning checksum radar icon */}
              <div style={{ position: 'relative', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{
                    animation: 'trophy-verify-spin 0.9s linear infinite',
                    filter: 'drop-shadow(0 0 4px rgba(56, 189, 248, 0.6))'
                  }}
                >
                  <circle cx="12" cy="12" r="9" stroke="rgba(56, 189, 248, 0.25)" strokeWidth="2.5" />
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>

              {/* Checksum text with animated trailing wave dots */}
              <span style={{ position: 'relative', zIndex: 1, letterSpacing: '0.2px' }}>
                Verifying Authenticity Checksum
              </span>
              <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex', letterSpacing: '1px', marginLeft: '-5px', fontWeight: 800 }}>
                <span style={{ animation: 'trophy-dot-blink 1.4s infinite 0s' }}>.</span>
                <span style={{ animation: 'trophy-dot-blink 1.4s infinite 0.25s' }}>.</span>
                <span style={{ animation: 'trophy-dot-blink 1.4s infinite 0.5s' }}>.</span>
              </span>

              {/* Micro live-activity ping dot */}
              <div style={{ position: 'relative', width: '8px', height: '8px', marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <div style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: '#38bdf8',
                  animation: 'trophy-radar-ping 1.4s cubic-bezier(0, 0, 0.2, 1) infinite'
                }} />
                <div style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  backgroundColor: '#38bdf8',
                  boxShadow: '0 0 6px #38bdf8'
                }} />
              </div>
            </div>
          )}

          {/* Action Buttons: Back, Share, Stats (Icon-only, hidden while verifying) */}
          {!verifying && (
            <div style={{
              width: `${dims.width}px`,
              maxWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '14px',
              gap: '12px'
            }}>
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  title="Back to Vault"
                  aria-label="Back to Vault"
                  data-testid="mobile-card-back-btn"
                  style={{
                    flex: '0 0 auto',
                    width: '44px',
                    height: '44px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    color: '#f8fafc',
                    borderRadius: '10px',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
                    transition: 'all 0.2s ease',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.28)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                  </svg>
                </button>
              )}
              <button
                type="button"
                data-testid="share-trophy-card-btn"
                onClick={() => setShowShareModal(true)}
                title="Share Trophy Card"
                aria-label="Share Trophy Card"
                style={{
                  flex: '0 0 auto',
                  width: '44px',
                  height: '44px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  color: '#f8fafc',
                  borderRadius: '10px',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.28)';
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.18)';
                  e.currentTarget.style.color = '#f8fafc';
                }}
              >
                <NeutralIcons.Share size={18} style={{ opacity: 0.95 }} />
              </button>
              {isOperatorUser && (
                <button
                  type="button"
                  data-testid="mobile-card-stats-btn"
                  onClick={() => setShowStatsModal(true)}
                  title="Stats"
                  aria-label="Stats"
                  style={{
                    flex: '0 0 auto',
                    width: '44px',
                    height: '44px',
                    background: 'linear-gradient(135deg, rgba(0, 198, 255, 0.16) 0%, rgba(0, 114, 255, 0.16) 100%)',
                    border: '1px solid rgba(0, 198, 255, 0.38)',
                    color: '#38bdf8',
                    borderRadius: '10px',
                    padding: 0,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 198, 255, 0.15)',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 198, 255, 0.25) 0%, rgba(0, 114, 255, 0.25) 100%)';
                    e.currentTarget.style.borderColor = 'rgba(0, 198, 255, 0.55)';
                    e.currentTarget.style.color = '#7dd3fc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 198, 255, 0.16) 0%, rgba(0, 114, 255, 0.16) 100%)';
                    e.currentTarget.style.borderColor = 'rgba(0, 198, 255, 0.38)';
                    e.currentTarget.style.color = '#38bdf8';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Serial Code & Timestamp */}
          <div style={{ marginTop: '16px', fontSize: '11px', color: '#64748b', textAlign: 'center' }}>
            Serial Code: <code style={{ color: '#94a3b8' }}>{coin.public_code}</code> &bull; Minted {coin.minted_at ? new Date(coin.minted_at).toLocaleDateString() : 'N/A'}
          </div>
        </div>

        {/* Slick Share Modal Overlay */}
        {showShareModal && (
          <div
            onClick={() => setShowShareModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              boxSizing: 'border-box'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: '420px',
                backgroundColor: '#0f172a',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '16px',
                padding: '22px',
                boxSizing: 'border-box',
                boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6)',
                color: '#f8fafc',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
            >
              {/* Modal Top Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {tenantIconUrl && (
                    <img
                      src={tenantIconUrl}
                      alt={tenantBrandName}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        objectFit: 'contain',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        padding: '3px',
                        flexShrink: 0
                      }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#ffffff' }}>
                      Share Trophy Card
                    </h3>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                      {cardTitle} &bull; Coin #{coin.coin_number}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowShareModal(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: 'none',
                    color: '#94a3b8',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <NeutralIcons.Close size={12} />
                </button>
              </div>

              {/* Social Link Preview Card */}
              <div style={{
                background: 'linear-gradient(145deg, #131d2e 0%, #0c1322 100%)',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '16px',
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)'
              }}>
                <div style={{
                  position: 'relative',
                  width: '64px',
                  height: '86px',
                  borderRadius: '6px',
                  background: '#090d16',
                  border: '1px solid rgba(212, 175, 55, 0.4)',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {coin.preview_front_url ? (
                    <img
                      src={coin.preview_front_url}
                      alt={cardTitle}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ fontSize: '26px' }}>🏆</span>
                  )}
                  {tenantIconUrl && (
                    <div style={{
                      position: 'absolute',
                      top: '3px',
                      left: '3px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '3px',
                      backgroundColor: 'rgba(15, 23, 42, 0.85)',
                      border: '1px solid rgba(212, 175, 55, 0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}>
                      <img
                        src={tenantIconUrl}
                        alt=""
                        style={{ width: '12px', height: '12px', objectFit: 'contain' }}
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {tenantIconUrl && (
                      <img
                        src={tenantIconUrl}
                        alt=""
                        style={{ width: '12px', height: '12px', objectFit: 'contain', flexShrink: 0 }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#38bdf8', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tenantBrandName} • Social Preview
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>
                    {cardTitle} • Official Trophy Card
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.4, marginTop: '2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    <span style={{ color: '#38bdf8', fontWeight: '600' }}>🏆 {payoffLine}</span>
                    {(coin?.card_data?.angler_name || coin?.card_data?.weight || coin?.card_data?.location) ? ' • ' : ''}
                    {coin?.card_data?.angler_name ? `Angler: ${coin.card_data.angler_name} • ` : ''}
                    {coin?.card_data?.weight ? `Weight: ${coin.card_data.weight} • ` : ''}
                    {coin?.card_data?.location ? `Location: ${coin.card_data.location}` : ''}
                  </div>

                  <div style={{ fontSize: '10px', color: '#34d399', fontWeight: '600', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>✓</span>
                    <span>Rich preview card & link enabled</span>
                  </div>
                </div>
              </div>

              {/* Copy Link Input & Button */}
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                  Public Card URL
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    readOnly
                    value={cleanCardUrl}
                    onClick={(e) => e.target.select()}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: 'rgba(0, 0, 0, 0.45)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      color: '#f8fafc',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleCopyShareLink}
                    style={{
                      background: shareCopied ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.12)',
                      border: shareCopied ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)',
                      color: shareCopied ? '#6ee7b7' : '#ffffff',
                      borderRadius: '8px',
                      padding: '8px 14px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      flexShrink: 0,
                      transition: 'all 0.2s'
                    }}
                  >
                    {shareCopied ? (
                      <>
                        <NeutralIcons.Check size={12} />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <NeutralIcons.Copy size={12} />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Native Mobile Share Button (if supported) */}
              {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
                <button
                  type="button"
                  onClick={handleNativeShare}
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    borderRadius: '10px',
                    padding: '11px 16px',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '18px',
                    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                    transition: 'all 0.2s'
                  }}
                >
                  <NeutralIcons.Share size={14} style={{ opacity: 0.9 }} />
                  <span>Share via Phone Apps (AirDrop, Messages, etc.)</span>
                </button>
              )}

              {/* Social Sharing Grid */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                  Share Directly
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {/* WhatsApp */}
                  <a
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${getPlatformShareUrl('whatsapp')}\n\n${shareText}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackEvent('share', { source: 'whatsapp', label: 'Card Shared to WhatsApp' })}
                    style={{
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      color: '#f1f5f9',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s'
                    }}
                  >
                    <NeutralIcons.WhatsApp size={16} style={{ opacity: 0.9 }} />
                    <span>WhatsApp</span>
                  </a>

                  {/* X (Twitter) */}
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(getPlatformShareUrl('x'))}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackEvent('share', { source: 'x', label: 'Card Shared to X (Twitter)' })}
                    style={{
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      color: '#f1f5f9',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s'
                    }}
                  >
                    <NeutralIcons.XTwitter size={14} style={{ opacity: 0.9 }} />
                    <span>Post (X)</span>
                  </a>

                  {/* Facebook */}
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getPlatformShareUrl('facebook'))}&quote=${encodeURIComponent(shareText)}&display=popup`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleFacebookShare(e)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      color: '#f1f5f9',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s',
                      cursor: 'pointer'
                    }}
                  >
                    <NeutralIcons.Facebook size={15} style={{ opacity: 0.9 }} />
                    <span>Facebook</span>
                  </a>

                  {/* Telegram */}
                  <a
                    href={`https://t.me/share/url?url=${encodeURIComponent(getPlatformShareUrl('telegram'))}&text=${encodeURIComponent(shareText)}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => trackEvent('share', { source: 'telegram', label: 'Card Shared to Telegram' })}
                    style={{
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      color: '#f1f5f9',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s'
                    }}
                  >
                    <NeutralIcons.Telegram size={15} style={{ opacity: 0.9 }} />
                    <span>Telegram</span>
                  </a>

                  {/* SMS */}
                  <a
                    href={`sms:?&body=${encodeURIComponent(`${shareText}\n\n${getPlatformShareUrl('sms')}`)}`}
                    onClick={() => trackEvent('share', { source: 'sms', label: 'Card Shared via SMS / Messages' })}
                    style={{
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      color: '#f1f5f9',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s'
                    }}
                  >
                    <NeutralIcons.Message size={15} style={{ opacity: 0.9 }} />
                    <span>Messages</span>
                  </a>

                  {/* Email */}
                  <a
                    href={`mailto:?subject=${encodeURIComponent(`Check out this Trophy Card: ${cardTitle}`)}&body=${encodeURIComponent(`${shareText}\n\n${getPlatformShareUrl('email')}`)}`}
                    onClick={() => trackEvent('share', { source: 'email', label: 'Card Shared via Email' })}
                    style={{
                      background: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      color: '#f1f5f9',
                      borderRadius: '10px',
                      padding: '10px 8px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.15s'
                    }}
                  >
                    <NeutralIcons.Mail size={15} style={{ opacity: 0.9 }} />
                    <span>Email</span>
                  </a>
                </div>
              </div>

              {/* On-Screen QR Code */}
              <div style={{
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <NeutralIcons.QrCodeIcon size={13} style={{ opacity: 0.85 }} />
                  <span>Scan with Phone Camera</span>
                </div>
                <div style={{
                  background: '#ffffff',
                  padding: '10px',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <QRCodeSVG value={getPlatformShareUrl('qrcode')} size={160} />
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '10px', lineHeight: 1.4 }}>
                  Scan with any iPhone or Android camera for instant card lookup
                </div>
              </div>

              {/* End-User Trophy Card Claiming Action */}
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>
                  Own this trophy but using a different account email?
                </div>

                {claimSuccess ? (
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid rgba(16, 185, 129, 0.35)',
                    borderRadius: '10px',
                    padding: '12px',
                    color: '#6ee7b7',
                    fontSize: '12px',
                    lineHeight: '1.4'
                  }}>
                    <div style={{ fontWeight: '700', marginBottom: '4px' }}>✓ Claim Token Dispatched!</div>
                    <div>{claimSuccess.message || `A verification token has been sent to ${claimSuccess.masked_email || "the owner's email"}.`}</div>
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#a7f3d0' }}>
                      Enter the 6-digit code in the <strong>Claim</strong> tab of your mobile vault to link this card.
                    </div>
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={handleRequestClaim}
                      disabled={claiming}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.25) 0%, rgba(180, 83, 9, 0.2) 100%)',
                        border: '1px solid rgba(245, 158, 11, 0.4)',
                        color: '#fef3c7',
                        borderRadius: '10px',
                        padding: '10px 16px',
                        fontSize: '12px',
                        fontWeight: '700',
                        cursor: claiming ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        <polyline points="9 12 11 14 15 10"></polyline>
                      </svg>
                      <span>{claiming ? 'Sending Verification Code...' : 'Claim This Trophy Card'}</span>
                    </button>
                    {claimError && (
                      <div style={{ marginTop: '6px', color: '#f87171', fontSize: '11px' }}>
                        {claimError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Card Stats Modal */}
        {showStatsModal && (
          <MobileCardStatsModal
            publicCode={coin.public_code}
            coin={coin}
            onClose={() => setShowStatsModal(false)}
          />
        )}
      </div>
    );
  }

  // =========================================================================
  // CASE A: UNISSUED VIEW - STEP 1: OPERATOR AUTH
  // =========================================================================
  if (authChecking) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0d14', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', color: '#38bdf8' }}>
            <NeutralIcons.Spinner size={40} />
          </div>
          <p style={{ marginTop: '12px', fontSize: '14px', color: '#94a3b8' }}>Verifying operator permissions...</p>
        </div>
      </div>
    );
  }

  if (!operatorToken || !isAuthorized) {
    const rawTenantLogo = coin?.tenant?.logo_url || coin?.organization?.logo_url;
    const tenantLogoUrl = rawTenantLogo && rawTenantLogo.startsWith('/uploads/') && API_BASE
      ? `${API_BASE.replace(/\/+$/, '')}${rawTenantLogo}`
      : rawTenantLogo;
    const tenantBrandName = coin?.tenant?.brand_name || coin?.organization?.name || 'TrophyCoin';

    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        background: '#0a0d14',
        color: '#fff',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 14px'
      }}>
        {/* Brand Logo Banner Above Login Panel */}
        {tenantLogoUrl && !tenantLogoFailed ? (
          <div style={{
            width: '100%',
            maxWidth: '380px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '18px',
            padding: '0 6px'
          }}>
            <img
              src={tenantLogoUrl}
              alt={`${tenantBrandName} Logo`}
              onError={() => setTenantLogoFailed(true)}
              style={{
                width: 'auto',
                maxWidth: '100%',
                maxHeight: '140px',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '12px',
                overflow: 'hidden',
                filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.4))'
              }}
            />
          </div>
        ) : (
          <div style={{
            width: '100%',
            maxWidth: '380px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '18px',
            padding: '0 6px'
          }}>
            <span style={{ fontSize: '32px', lineHeight: 1 }}>🏆</span>
            <span style={{ fontSize: '22px', fontWeight: 700, color: '#f8fafc', letterSpacing: '-0.3px' }}>
              {tenantBrandName}
            </span>
          </div>
        )}

        {/* Login Panel */}
        <div style={{
          width: '100%',
          maxWidth: '380px',
          boxSizing: 'border-box',
          background: '#131824',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '24px 20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <span style={{ fontSize: '11px', background: 'rgba(234, 179, 8, 0.15)', border: '1px solid rgba(234, 179, 8, 0.3)', color: '#facc15', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>
              Unissued Physical Coin
            </span>
            <p style={{ margin: '10px 0 0 0', fontSize: '13px', color: '#94a3b8', lineHeight: 1.4 }}>
              This physical trophy coin is ready to be personalized and issued. Sign in with your operator credentials to begin.
            </p>
          </div>

          {authError && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#fca5a5',
              padding: '12px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              marginBottom: '16px',
              lineHeight: 1.45
            }}>
              <div style={{ fontWeight: 700, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', color: '#f87171' }}>
                <span>🔒 Authorization Denied</span>
              </div>
              <div>{authError}</div>
              {operatorToken && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={handleOperatorLogout}
                    style={{
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(248, 113, 113, 0.4)',
                      borderRadius: '4px',
                      color: '#fca5a5',
                      cursor: 'pointer'
                    }}
                  >
                    Clear Session & Sign In With Another Account
                  </button>
                </div>
              )}
            </div>
          )}
          {loginError && (
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.35)', color: '#fca5a5', padding: '10px', borderRadius: '8px', fontSize: '12px', marginBottom: '16px' }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleOperatorLogin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>Account Email</label>
              <input
                type="email"
                required
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@organization.com"
                style={{
                  background: 'rgba(0,0,0,0.35)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#cbd5e1' }}>Password</label>
              <input
                type="password"
                required
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  background: 'rgba(0,0,0,0.35)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              style={{
                background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '11px',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                marginTop: '6px'
              }}
            >
              {loginLoading ? 'Authenticating...' : (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <span>Sign In & Configure Coin</span>
                  <NeutralIcons.ArrowRight size={14} />
                </span>
              )}
            </button>
          </form>

          {/* Tenant Onboarding / Form Wizard Video Tutorial Link */}
          <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
            <a
              href={coin?.tenant?.id ? `/tutorial?tenant_id=${coin.tenant.id}&from=login` : '/tutorial?from=login'}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                color: '#38bdf8',
                fontSize: '12px',
                fontWeight: 600,
                textDecoration: 'none',
                opacity: 0.9,
                transition: 'opacity 0.2s',
                padding: '4px 8px',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.textDecoration = 'none'; }}
            >
              <NeutralIcons.Video size={14} />
              <span>Need help? Watch video: How to log coin data →</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // CASE A: UNISSUED VIEW - STEP 2: WIZARD & 3D PREVIEW
  // =========================================================================
  const totalPages = wizardPages.length;
  const isPreviewPage = currentPageIndex === totalPages; // Page totalPages is the 3D preview
  const currentPage = wizardPages[currentPageIndex];

  const currentZoom = activeImageElem?.imgZoom ?? 1;
  const currentPanX = activeImageElem?.imgPanX ?? 0;
  const currentPanY = activeImageElem?.imgPanY ?? 0;


  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: '100dvh',
      minHeight: '-webkit-fill-available',
      width: '100%',
      maxWidth: '100%',
      background: '#07090e',
      color: '#fff',
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: isMobilePhone ? 'stretch' : 'center',
      justifyContent: 'flex-start',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Keyframe Styles for Scroll Overflow Indicator */}
      <style>{`
        @keyframes bounceDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(3px); }
        }
        @keyframes floatPillFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Phone App Container Frame */}
      <div style={{
        width: isMobilePhone ? '100%' : '440px',
        maxWidth: '100%',
        minWidth: 0,
        height: '100%',
        maxHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: '#131824',
        borderLeft: isMobilePhone ? 'none' : '1px solid rgba(255,255,255,0.06)',
        borderRight: isMobilePhone ? 'none' : '1px solid rgba(255,255,255,0.06)',
        position: 'relative',
        boxSizing: 'border-box',
        overflow: 'hidden',
        margin: '0 auto'
      }}>
        {/* 1. FIXED TOP APP BAR & HEADER (Does not scroll) */}
        <div style={{
          flexShrink: 0,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          background: 'rgba(19, 24, 36, 0.98)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          paddingTop: 'max(14px, env(safe-area-inset-top, 0px))',
          paddingBottom: (!isPreviewPage || isPlacingPicture) ? '10px' : '8px',
          paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
          boxSizing: 'border-box',
          zIndex: 20
        }}>
          {/* Top Operator Info & Sign Out */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: (!isPreviewPage || isPlacingPicture) ? '8px' : '0px',
            width: '100%',
            minWidth: 0,
            gap: '8px'
          }}>
            <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Operator: <span style={{ color: '#cbd5e1', fontWeight: 600 }}>{operatorUser?.name || 'Authorized'}</span> {orgData?.name ? `(${orgData.name})` : ''}
              </div>
            </div>
            <button
              onClick={handleOperatorLogout}
              style={{ flexShrink: 0, background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer' }}
            >
              Sign Out
            </button>
          </div>

          {/* Progress Bar (Hidden during Picture Placement and on 3D Preview Page to maximize screen space) */}
          {!isPlacingPicture && !isPreviewPage && (
            <div style={{ marginBottom: '8px', width: '100%', minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 700, color: '#94a3b8', marginBottom: '4px', width: '100%', minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px', minWidth: 0 }}>
                  {`Step ${currentPageIndex + 1} of ${totalPages + 1}: ${currentPage?.title}`}
                </span>
                <span style={{ flexShrink: 0 }}>{Math.round(((currentPageIndex + 1) / (totalPages + 1)) * 100)}%</span>
              </div>
              <div style={{ width: '100%', height: '5px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${((currentPageIndex + 1) / (totalPages + 1)) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #00c6ff, #0072ff)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}

          {/* Current Dataset Type Title Badge (Displays only during active datasets or picture placement) */}
          {(!isPreviewPage || isPlacingPicture) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              marginTop: '2px'
            }}>
              {isPlacingPicture ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  boxSizing: 'border-box',
                  gap: '6px',
                  padding: '5px 12px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  background: 'rgba(56, 189, 248, 0.18)',
                  border: '1px solid #38bdf8',
                  color: '#38bdf8'
                }}>
                  <NeutralIcons.Crop size={13} style={{ flexShrink: 0 }} />
                  <span>Place Picture Mode</span>
                </div>
              ) : currentPage ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  boxSizing: 'border-box',
                  gap: '6px',
                  padding: '5px 12px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  background: 'rgba(0, 198, 255, 0.18)',
                  border: '1px solid #00c6ff',
                  color: '#38bdf8'
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentPageIndex + 1}. {currentPage.title}
                  </span>
                  {currentPage.fields?.length > 0 && (
                    <span style={{ fontSize: '10px', opacity: 0.75, fontWeight: 500, flexShrink: 0 }}>
                      ({currentPage.fields.length} {currentPage.fields.length === 1 ? 'field' : 'fields'})
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* 2. MIDDLE AREA (Scrollable Datasets Panel + Floating Overflow Indicator) */}
        <div style={{
          flex: 1,
          position: 'relative',
          minHeight: 0,
          width: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Scrollable Datasets Panel */}
          <div
            ref={wizardScrollRef}
            onScroll={checkScrollOverflow}
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              paddingTop: '16px',
              paddingBottom: (hasScrollOverflow && !isPreviewPage) ? '54px' : '24px',
              paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
              boxSizing: 'border-box',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0
            }}
          >
          {/* Validation Error Banner */}
          {validationError && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#fca5a5',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <NeutralIcons.Alert size={14} style={{ flexShrink: 0 }} />
              <span>{validationError}</span>
            </div>
          )}

          {/* Wizard Page Fields (when NOT in preview page) */}
          {!isPreviewPage && currentPage && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              padding: '0 2px',
              boxSizing: 'border-box',
              width: '100%',
              maxWidth: '100%',
              minWidth: 0
            }}>
              {currentPage.fields.map(field => {
                const rawValue = formData[field.key];
                const hasValue = rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== '';
                const isExifMapped = Boolean(field.mappingKey && field.mappingKey.startsWith('photo_exif.'));
                const isExplicitLock = Boolean(
                  (field.lockedValue !== null && field.lockedValue !== undefined && field.lockedValue !== '') ||
                  field.isExplicitlyLocked
                );
                // Revert to manual editable if field is mapped to photo EXIF (always editable) OR mapped to data that has no value
                const isReadOnly = isExplicitLock || (Boolean(field.mappingKey || field.isMapped) && !isExifMapped && hasValue);
                const isMissing = validationError && field.required && !hasValue;
                const inputStyle = {
                  background: isReadOnly ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.35)',
                  border: `1px solid ${isMissing ? '#ef4444' : (isReadOnly ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.12)')}`,
                  borderRadius: '8px',
                  padding: '9px 12px',
                  color: isReadOnly ? '#94a3b8' : '#fff',
                  WebkitTextFillColor: isReadOnly ? '#94a3b8' : '#fff',
                  fontSize: '16px', // 16px is required on mobile to prevent iOS Safari auto-zooming and expanding the page
                  outline: 'none',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  cursor: isReadOnly ? 'not-allowed' : 'text',
                  fontFamily: 'inherit'
                };

                return (
                  <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', minWidth: 0 }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: isMissing ? '#fca5a5' : '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{field.label}</span>
                        {field.required && !isReadOnly && <span style={{ color: '#ef4444' }}>*</span>}
                        {field.option_source === 'species_library' && (
                          <span style={{ fontSize: '9px', fontWeight: 600, color: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.12)', border: '1px solid rgba(56, 189, 248, 0.25)', padding: '1px 4px', borderRadius: '3px' }}>
                            🌿 Species Registry
                          </span>
                        )}
                      </label>
                      {isReadOnly ? (
                        <span style={{
                          fontSize: '10px',
                          color: field.mappingKey ? '#38bdf8' : '#94a3b8',
                          background: field.mappingKey ? 'rgba(0, 198, 255, 0.12)' : 'rgba(255,255,255,0.06)',
                          border: field.mappingKey ? '1px solid rgba(0, 198, 255, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0
                        }}>
                          {isExplicitLock ? (
                            <>
                              <NeutralIcons.Lock size={10} style={{ flexShrink: 0 }} />
                              <span>Locked</span>
                            </>
                          ) : (
                            <>
                              <NeutralIcons.Link size={10} style={{ flexShrink: 0 }} />
                              <span>Mapped (Read Only)</span>
                            </>
                          )}
                        </span>
                      ) : (
                        <>
                          {isExifMapped ? (
                            <span style={{
                              fontSize: '10px',
                              color: hasValue ? '#38bdf8' : '#fbbf24',
                              background: hasValue ? 'rgba(56, 189, 248, 0.12)' : 'rgba(251, 191, 36, 0.12)',
                              border: hasValue ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid rgba(251, 191, 36, 0.3)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              flexShrink: 0
                            }}>
                              {hasValue ? '📍 EXIF Mapped (Editable)' : '✏️ Manual Input (No EXIF)'}
                            </span>
                          ) : field.mappingKey && !hasValue ? (
                            <span style={{
                              fontSize: '10px',
                              color: '#fbbf24',
                              background: 'rgba(251, 191, 36, 0.12)',
                              border: '1px solid rgba(251, 191, 36, 0.3)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              flexShrink: 0
                            }}>
                              ✏️ Manual Input
                            </span>
                          ) : null}
                        </>
                      )}
                    </div>

                    {field.type === 'options' ? (
                      <select
                        disabled={isReadOnly}
                        value={formData[field.key] || ''}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setFormData({ ...formData, [field.key]: e.target.value });
                          if (validationError) setValidationError('');
                        }}
                        style={{ ...inputStyle, cursor: isReadOnly ? 'not-allowed' : 'pointer', appearance: 'auto' }}
                      >
                        <option value="" style={{ background: '#1a1e29', color: '#94a3b8' }}>
                          {field.options && field.options.length > 0 ? `Select ${field.label}...` : 'Select option...'}
                        </option>
                        {(field.options || []).map((opt, oIdx) => (
                          <option key={oIdx} value={opt} style={{ background: '#1a1e29', color: '#fff' }}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        rows={3}
                        readOnly={isReadOnly}
                        disabled={isReadOnly}
                        tabIndex={isReadOnly ? -1 : 0}
                        value={formData[field.key] || ''}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setFormData({ ...formData, [field.key]: e.target.value });
                          if (validationError) setValidationError('');
                        }}
                        placeholder={field.sampleValue ? `e.g. ${field.sampleValue}` : (field.description || `Enter ${field.label}...`)}
                        style={{ ...inputStyle, minHeight: '68px', resize: isReadOnly ? 'none' : 'vertical' }}
                      />
                    ) : field.type === 'image' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', boxSizing: 'border-box' }}>
                        {formData[field.key] && (
                          <div style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}>
                            <img src={formData[field.key]} alt="Upload Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            {!isReadOnly && (
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => {
                                    const next = { ...prev };
                                    delete next[field.key];
                                    delete next[`${field.key}_original`];
                                    delete next[`${field.key}_exif`];
                                    return next;
                                  });
                                  setImageExifMap(prev => {
                                    const next = { ...prev };
                                    delete next[field.key];
                                    return next;
                                  });
                                }}
                                style={{
                                  position: 'absolute',
                                  top: 2,
                                  right: 2,
                                  background: 'rgba(0,0,0,0.7)',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '20px',
                                  height: '20px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <NeutralIcons.Close size={10} />
                              </button>
                            )}
                          </div>
                        )}

                        {/* EXIF Metadata Table */}
                        {formData[field.key] && (() => {
                          const currentExif = imageExifMap[field.key] || formData[`${field.key}_exif`] || null;
                          const mappedTargets = getExifMappingTargets();

                          const renderUsageBadge = (exifValue, targetField) => {
                            if (!targetField) {
                              return (
                                <span style={{ color: '#64748b', fontSize: '10px' }}>
                                  — Not mapped
                                </span>
                              );
                            }
                            const hasExifVal = Boolean(exifValue && String(exifValue).trim() !== '');
                            if (hasExifVal) {
                              return (
                                <span style={{
                                  color: '#4ade80',
                                  backgroundColor: 'rgba(34, 197, 94, 0.12)',
                                  border: '1px solid rgba(34, 197, 94, 0.28)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  ✓ Mapped to {targetField.label} (Used)
                                </span>
                              );
                            } else {
                              return (
                                <span style={{
                                  color: '#fbbf24',
                                  backgroundColor: 'rgba(251, 191, 36, 0.12)',
                                  border: '1px solid rgba(251, 191, 36, 0.28)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  ⚠️ Mapped to {targetField.label} (Reverted to manual)
                                </span>
                              );
                            }
                          };

                          const rows = [
                            {
                              prop: 'Date',
                              icon: '📅',
                              val: currentExif?.date,
                              fallbackText: 'Not in photo',
                              target: mappedTargets.date
                            },
                            {
                              prop: 'Time',
                              icon: '🕒',
                              val: currentExif?.time,
                              fallbackText: 'Not in photo',
                              target: mappedTargets.time
                            },
                            {
                              prop: 'Location',
                              icon: '📍',
                              val: currentExif?.location,
                              fallbackText: 'No GPS in photo',
                              target: mappedTargets.location
                            },
                            {
                              prop: 'Place',
                              icon: '🌍',
                              val: currentExif?.isResolvingPlace ? 'Resolving place...' : (currentExif?.place || (currentExif?.altitude ? `Alt: ${currentExif.altitude}` : null)),
                              fallbackText: 'Not in photo',
                              target: mappedTargets.place
                            }
                          ];

                          return (
                            <div
                              data-testid="exif-metadata-table"
                              style={{
                                width: '100%',
                                backgroundColor: 'rgba(15, 23, 42, 0.75)',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                marginTop: '4px',
                                boxSizing: 'border-box'
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '6px 10px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '12px' }}>📷</span>
                                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#f8fafc' }}>Photo EXIF Metadata</span>
                                </div>
                                {currentExif?.hasExif ? (
                                  <span style={{
                                    fontSize: '9px',
                                    color: '#4ade80',
                                    backgroundColor: 'rgba(34, 197, 94, 0.15)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: '10px',
                                    padding: '1px 7px',
                                    fontWeight: 700
                                  }}>
                                    ✓ EXIF Detected
                                  </span>
                                ) : (
                                  <span style={{
                                    fontSize: '9px',
                                    color: '#fbbf24',
                                    backgroundColor: 'rgba(251, 191, 36, 0.12)',
                                    border: '1px solid rgba(251, 191, 36, 0.25)',
                                    borderRadius: '10px',
                                    padding: '1px 7px',
                                    fontWeight: 600
                                  }}>
                                    No EXIF in image
                                  </span>
                                )}
                              </div>

                              <div style={{ width: '100%', overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '280px' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)', color: '#94a3b8', fontSize: '10px' }}>
                                      <th style={{ padding: '5px 8px', fontWeight: 600, width: '24%' }}>Property</th>
                                      <th style={{ padding: '5px 8px', fontWeight: 600, width: '38%' }}>EXIF Value</th>
                                      <th style={{ padding: '5px 8px', fontWeight: 600, width: '38%' }}>Mapping & Usage</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((r, rIdx) => {
                                      const hasVal = Boolean(r.val && String(r.val).trim() !== '');
                                      return (
                                        <tr
                                          key={r.prop}
                                          style={{
                                            borderBottom: rIdx < rows.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                                            backgroundColor: rIdx % 2 === 0 ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
                                            fontSize: '11px'
                                          }}
                                        >
                                          <td style={{ padding: '5px 8px', color: '#e2e8f0', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            <span style={{ marginRight: '4px' }}>{r.icon}</span>
                                            {r.prop}
                                          </td>
                                          <td style={{ padding: '5px 8px', color: hasVal ? '#f1f5f9' : '#64748b', fontFamily: (r.prop === 'Location' || r.prop === 'Time') && hasVal ? 'monospace' : 'inherit' }}>
                                            {hasVal ? r.val : <span style={{ fontStyle: 'italic' }}>{r.fallbackText}</span>}
                                          </td>
                                          <td style={{ padding: '5px 8px' }}>
                                            {renderUsageBadge(r.val, r.target)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })()}

                        {!formData[field.key] && (
                          <input
                            type="file"
                            accept="image/*"
                            disabled={isReadOnly}
                            onChange={(e) => {
                              if (isReadOnly) return;
                              const file = e.target.files && e.target.files[0];
                              if (file) {
                                // Extract EXIF metadata
                                extractExifFromBlob(file).then(async (exif) => {
                                  if (exif) {
                                    const dateVal = exif.date || (exif.date_taken ? exif.date_taken.split(/[\sT]/)[0] : null);
                                    const timeVal = exif.time || (exif.date_taken && exif.date_taken.includes(' ') ? exif.date_taken.split(' ')[1] : null);
                                    const locVal = exif.gps_coordinates || (exif.gps_latitude && exif.gps_longitude ? `${exif.gps_latitude}, ${exif.gps_longitude}` : null);
                                    const altVal = exif.gps_altitude || null;

                                    const metaObj = {
                                      hasExif: Boolean(dateVal || timeVal || locVal || altVal),
                                      date: dateVal,
                                      time: timeVal,
                                      location: locVal,
                                      altitude: altVal,
                                      place: null,
                                      isResolvingPlace: Boolean(exif.gps_latitude && exif.gps_longitude),
                                      rawExif: exif
                                    };

                                    setImageExifMap(prev => ({ ...prev, [field.key]: metaObj }));

                                    // Auto-populate mapped fields in formData
                                    const allFields = wizardPages.flatMap(p => p.fields || []);
                                    const formUpdates = {
                                      [`${field.key}_exif`]: metaObj
                                    };

                                    allFields.forEach(f => {
                                      const m = f.mappingKey || '';
                                      if (m === 'photo_exif.gps_coordinates' && locVal) {
                                        formUpdates[f.key] = locVal;
                                      } else if (m === 'photo_exif.gps_latitude' && exif.gps_latitude) {
                                        formUpdates[f.key] = exif.gps_latitude;
                                      } else if (m === 'photo_exif.gps_longitude' && exif.gps_longitude) {
                                        formUpdates[f.key] = exif.gps_longitude;
                                      } else if (m === 'photo_exif.gps_dms' && exif.gps_dms) {
                                        formUpdates[f.key] = exif.gps_dms;
                                      } else if (m === 'photo_exif.gps_altitude' && altVal) {
                                        formUpdates[f.key] = altVal;
                                      } else if (m === 'photo_exif.place' && exif.place) {
                                        formUpdates[f.key] = exif.place;
                                      } else if (m === 'photo_exif.time') {
                                        if (timeVal) {
                                          formUpdates[f.key] = f.dateFormat ? formatDateTimeValue(timeVal, f.dateFormat) : timeVal;
                                        }
                                      } else if (m === 'photo_exif.date') {
                                        if (dateVal) {
                                          formUpdates[f.key] = f.dateFormat ? formatDateTimeValue(dateVal, f.dateFormat) : dateVal;
                                        }
                                      } else if (m === 'photo_exif.date_taken') {
                                        const pickerType = getPickerTypeForFormat(f.type, f.dateFormat, f.label, f.alias);
                                        const isTimeField = f.type === 'time' || pickerType === 'time' || /(?:^|_|\b)time(?:$|_|\b)/i.test(`${f.key} ${f.label} ${f.alias || ""}`);
                                        const isDateField = f.type === 'date' || pickerType === 'date' || /(?:^|_|\b)date(?:$|_|\b)/i.test(`${f.key} ${f.label} ${f.alias || ""}`);

                                        if (isTimeField && !isDateField && timeVal) {
                                          formUpdates[f.key] = f.dateFormat ? formatDateTimeValue(timeVal, f.dateFormat) : timeVal;
                                        } else if (isDateField && !isTimeField && dateVal) {
                                          formUpdates[f.key] = f.dateFormat ? formatDateTimeValue(dateVal, f.dateFormat) : dateVal;
                                        } else if (exif.date_taken) {
                                          formUpdates[f.key] = f.dateFormat ? formatDateTimeValue(exif.date_taken, f.dateFormat) : exif.date_taken;
                                        }
                                      }
                                    });

                                    setFormData(prev => ({ ...prev, ...formUpdates }));

                                    // Background reverse geocode for place name
                                    if (exif.gps_latitude && exif.gps_longitude) {
                                      const placeName = await reverseGeocodePlace(exif.gps_latitude, exif.gps_longitude);
                                      if (placeName) {
                                        setImageExifMap(prev => ({
                                          ...prev,
                                          [field.key]: {
                                            ...prev[field.key],
                                            place: placeName,
                                            isResolvingPlace: false
                                          }
                                        }));
                                        setFormData(prev => ({
                                          ...prev,
                                          [`${field.key}_exif`]: {
                                            ...(prev[`${field.key}_exif`] || metaObj),
                                            place: placeName,
                                            isResolvingPlace: false
                                          }
                                        }));

                                        allFields.forEach(f => {
                                          const m = f.mappingKey || '';
                                          if (m === 'photo_exif.place') {
                                            setFormData(prev => ({ ...prev, [f.key]: placeName }));
                                          } else if ((m === 'country' || m === 'location' || m === 'harvest_country') && (!formData[f.key] || formData[f.key] === '')) {
                                            setFormData(prev => prev[f.key] ? prev : ({ ...prev, [f.key]: placeName }));
                                          }
                                        });
                                      } else {
                                        setImageExifMap(prev => ({
                                          ...prev,
                                          [field.key]: {
                                            ...prev[field.key],
                                            place: altVal ? `Alt: ${altVal}` : 'Location detected',
                                            isResolvingPlace: false
                                          }
                                        }));
                                      }
                                    }
                                  } else {
                                    const emptyMeta = {
                                      hasExif: false,
                                      date: null,
                                      time: null,
                                      location: null,
                                      altitude: null,
                                      place: null,
                                      isResolvingPlace: false,
                                      rawExif: null
                                    };
                                    setImageExifMap(prev => ({ ...prev, [field.key]: emptyMeta }));
                                    setFormData(prev => ({ ...prev, [`${field.key}_exif`]: emptyMeta }));
                                  }
                                }).catch(err => {
                                  console.warn('[EXIF] Extraction error:', err);
                                  const emptyMeta = {
                                    hasExif: false,
                                    date: null,
                                    time: null,
                                    location: null,
                                    altitude: null,
                                    place: null,
                                    isResolvingPlace: false,
                                    rawExif: null
                                  };
                                  setImageExifMap(prev => ({ ...prev, [field.key]: emptyMeta }));
                                  setFormData(prev => ({ ...prev, [`${field.key}_exif`]: emptyMeta }));
                                });

                                const reader = new FileReader();
                                reader.onload = (loadEvt) => {
                                  const rawResult = loadEvt.target.result;
                                  if (typeof Image !== 'undefined') {
                                    const probe = new Image();
                                    probe.onload = () => {
                                      try {
                                        const maxDim = 2048;
                                        let w = probe.naturalWidth || probe.width;
                                        let h = probe.naturalHeight || probe.height;
                                        if (w > maxDim || h > maxDim) {
                                          const scale = maxDim / Math.max(w, h);
                                          w = Math.round(w * scale);
                                          h = Math.round(h * scale);
                                          const canvas = document.createElement('canvas');
                                          canvas.width = w;
                                          canvas.height = h;
                                          const ctx = canvas.getContext('2d');
                                          ctx.drawImage(probe, 0, 0, w, h);
                                          const optDataUrl = canvas.toDataURL('image/jpeg', 0.90);
                                          setFormData((prev) => ({
                                            ...prev,
                                            [field.key]: optDataUrl,
                                            [`${field.key}_original`]: rawResult
                                          }));
                                          if (validationError) setValidationError('');
                                          return;
                                        }
                                      } catch (canvasErr) {
                                        console.warn('[MobileCoinCardView] Canvas optimize notice:', canvasErr);
                                      }
                                      setFormData((prev) => ({ ...prev, [field.key]: rawResult }));
                                      if (validationError) setValidationError('');
                                    };
                                    probe.onerror = () => {
                                      setFormData((prev) => ({ ...prev, [field.key]: rawResult }));
                                      if (validationError) setValidationError('');
                                    };
                                    probe.src = rawResult;
                                  } else {
                                    setFormData((prev) => ({ ...prev, [field.key]: rawResult }));
                                    if (validationError) setValidationError('');
                                  }
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            style={{ ...inputStyle, padding: '6px', fontSize: '14px' }}
                          />
                        )}
                      </div>
                    ) : (() => {
                      const pickerType = getPickerTypeForFormat(field.type, field.dateFormat, field.label, field.alias);
                      if (pickerType) {
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                              <input
                                type="text"
                                readOnly={isReadOnly}
                                disabled={isReadOnly}
                                tabIndex={isReadOnly ? -1 : 0}
                                value={formData[field.key] || ''}
                                onChange={(e) => {
                                  if (isReadOnly) return;
                                  setFormData({ ...formData, [field.key]: e.target.value });
                                  if (validationError) setValidationError('');
                                }}
                                placeholder={field.dateFormat ? `Format: ${field.dateFormat}` : (field.sampleValue ? `e.g. ${field.sampleValue}` : `Select or enter ${field.label}...`)}
                                style={{
                                  ...inputStyle,
                                  paddingRight: isReadOnly ? '12px' : '42px',
                                  margin: 0
                                }}
                              />
                              {!isReadOnly && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: '6px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                >
                                  <input
                                    type={pickerType}
                                    tabIndex={-1}
                                    aria-label={`Pick ${field.label}`}
                                    value={toPickerInputValue(formData[field.key], pickerType)}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      opacity: 0,
                                      cursor: 'pointer',
                                      zIndex: 3
                                    }}
                                    onChange={(e) => {
                                      const rawVal = e.target.value;
                                      if (!rawVal) return;
                                      const fmt = field.dateFormat || getDefaultFormat(field.type, field.label, field.alias);
                                      const formatted = fmt ? formatDateTimeValue(rawVal, fmt) : rawVal;
                                      setFormData(prev => ({ ...prev, [field.key]: formatted }));
                                      if (validationError) setValidationError('');
                                    }}
                                  />
                                  <div
                                    style={{
                                      pointerEvents: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '28px',
                                      height: '28px',
                                      borderRadius: '6px',
                                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                      color: '#94a3b8',
                                      zIndex: 1
                                    }}
                                  >
                                    {pickerType === 'time' ? <NeutralIcons.Clock size={15} /> : <NeutralIcons.Calendar size={15} />}
                                  </div>
                                </div>
                              )}
                            </div>
                            {field.dateFormat && (
                              <span style={{ fontSize: '10px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {pickerType === 'time' ? <NeutralIcons.Clock size={11} /> : <NeutralIcons.Calendar size={11} />}
                                </span>
                                <span>Format: <code>{field.dateFormat}</code></span>
                              </span>
                            )}
                          </div>
                        );
                      }

                      if (isCoordinateField(field)) {
                        const isLocatingThis = locatingFieldKey === field.key;
                        const coordFmt = (field.validation?.format || 'dd').toUpperCase();
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                            <input
                              type="text"
                              readOnly={isReadOnly}
                              disabled={isReadOnly}
                              tabIndex={isReadOnly ? -1 : 0}
                              value={formData[field.key] || ''}
                              onChange={(e) => {
                                if (isReadOnly) return;
                                setFormData({ ...formData, [field.key]: e.target.value });
                                if (validationError) setValidationError('');
                              }}
                              placeholder={field.sampleValue ? `e.g. ${field.sampleValue}` : '-17.118579, 24.054117'}
                              style={inputStyle}
                            />

                            {!isReadOnly && (
                              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                <button
                                  type="button"
                                  disabled={isLocatingThis}
                                  onClick={() => handleAcquireDeviceLocation(field)}
                                  style={{
                                    flex: 1,
                                    padding: '8px 10px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(56, 189, 248, 0.1)',
                                    border: '1px solid rgba(56, 189, 248, 0.3)',
                                    color: '#38bdf8',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    cursor: isLocatingThis ? 'wait' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  {isLocatingThis ? (
                                    <>
                                      <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                                      <span>Locating...</span>
                                    </>
                                  ) : (
                                    <>
                                      <span>📍</span>
                                      <span>Current Location</span>
                                    </>
                                  )}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleOpenMapPicker(field)}
                                  style={{
                                    flex: 1,
                                    padding: '8px 10px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(168, 85, 247, 0.1)',
                                    border: '1px solid rgba(168, 85, 247, 0.3)',
                                    color: '#c084fc',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <span>🗺️</span>
                                  <span>Pin on Map</span>
                                </button>
                              </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: '#64748b', padding: '0 2px' }}>
                              <span>Format: <code>{coordFmt}</code></span>
                              {!isReadOnly && <span>Tap button to acquire GPS or pin</span>}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <input
                          type={field.type === 'number' ? 'number' : (field.type === 'mobile' || field.type === 'tel' ? 'tel' : 'text')}
                          step={field.type === 'number' ? 'any' : undefined}
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                          tabIndex={isReadOnly ? -1 : 0}
                          value={formData[field.key] || ''}
                          onChange={(e) => {
                            if (isReadOnly) return;
                            setFormData({ ...formData, [field.key]: e.target.value });
                            if (validationError) setValidationError('');
                          }}
                          placeholder={field.sampleValue ? `e.g. ${field.sampleValue}` : (field.description || `Enter ${field.label}...`)}
                          style={inputStyle}
                        />
                      );
                    })()}

                    {field.description && (
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        {field.description}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 3D Review Step (when in preview page) */}
          {isPreviewPage && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 2px 24px 2px', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
              <ResponsiveFlippableCard
                template={template}
                cardData={formData}
                isFlipped={isFlipped}
                onFlip={() => setIsFlipped(!isFlipped)}
                frontCardRef={frontCardRef}
                backCardRef={backCardRef}
                datasets={datasets}
                user={operatorUser}
                orgData={orgData}
                tenantConfig={coin?.tenant}
                isPlacingPicture={isPlacingPicture}
                onPlacePicture={() => setIsPlacingPicture(true)}
                onPlacementChange={handleUpdatePlacement}
                activeImageElem={activeImageElem}
              />
            </div>
          )}
        </div>

        {/* Floating Overflow Indicator (Appears when fields overflow screen height) */}
        {hasScrollOverflow && !isPreviewPage && (
          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none',
              zIndex: 15
            }}
          >
            <button
              type="button"
              onClick={handleScrollDownIndicator}
              aria-label="Scroll down for more fields"
              style={{
                pointerEvents: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                background: 'rgba(15, 23, 42, 0.92)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(0, 198, 255, 0.5)',
                borderRadius: '20px',
                color: '#38bdf8',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 18px rgba(0,0,0,0.65), 0 0 10px rgba(0, 198, 255, 0.25)',
                animation: 'floatPillFadeIn 0.2s ease-out forwards',
                whiteSpace: 'nowrap',
                userSelect: 'none'
              }}
            >
              <span>More fields below</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', animation: 'bounceDown 1.4s ease-in-out infinite' }}>
                <NeutralIcons.ArrowDown size={13} />
              </span>
            </button>
          </div>
        )}
      </div>

        {/* 3. PINNED BOTTOM FOOTER NAVIGATION (Firmly docked, does not move) */}
        <div style={{
          flexShrink: 0,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          background: 'rgba(15, 19, 30, 0.98)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: isPlacingPicture ? '10px' : '12px',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          alignItems: 'center',
          zIndex: 20
        }}>
          {isPlacingPicture ? (
            <div style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {/* Row 1: Zoom Control */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', width: '42px', flexShrink: 0 }}>
                  Zoom:
                </span>
                <button
                  type="button"
                  onClick={() => handleUpdatePlacement({ imgZoom: Math.max(0.5, Math.round((currentZoom - 0.1) * 100) / 100) })}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  −
                </button>
                <input
                  type="range"
                  min="0.5"
                  max="3.0"
                  step="0.02"
                  value={currentZoom}
                  onChange={(e) => handleUpdatePlacement({ imgZoom: parseFloat(e.target.value) })}
                  style={{
                    flex: 1,
                    accentColor: '#38bdf8',
                    cursor: 'pointer',
                    height: '6px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleUpdatePlacement({ imgZoom: Math.min(3.0, Math.round((currentZoom + 0.1) * 100) / 100) })}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  +
                </button>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', width: '38px', textAlign: 'right', flexShrink: 0 }}>
                  {Math.round(currentZoom * 100)}%
                </span>
              </div>

              {/* Row 2: Position Pan Sliders */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', width: '14px', flexShrink: 0 }}>X:</span>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    step="1"
                    value={currentPanX}
                    onChange={(e) => handleUpdatePlacement({ imgPanX: parseInt(e.target.value, 10) })}
                    style={{ flex: 1, accentColor: '#38bdf8', cursor: 'pointer', height: '5px' }}
                  />
                  <span style={{ fontSize: '10px', color: '#64748b', width: '28px', textAlign: 'right', flexShrink: 0 }}>
                    {currentPanX}%
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', width: '14px', flexShrink: 0 }}>Y:</span>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    step="1"
                    value={currentPanY}
                    onChange={(e) => handleUpdatePlacement({ imgPanY: parseInt(e.target.value, 10) })}
                    style={{ flex: 1, accentColor: '#38bdf8', cursor: 'pointer', height: '5px' }}
                  />
                  <span style={{ fontSize: '10px', color: '#64748b', width: '28px', textAlign: 'right', flexShrink: 0 }}>
                    {currentPanY}%
                  </span>
                </div>
              </div>

              {/* Row 3: Nudge Arrows, Quick Actions & Done Button */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%', paddingTop: '2px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    title="Nudge Left"
                    onClick={() => handleUpdatePlacement({ imgPanX: Math.max(-100, currentPanX - 5) })}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cbd5e1',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <NeutralIcons.ChevronLeft size={13} />
                  </button>
                  <button
                    type="button"
                    title="Nudge Up"
                    onClick={() => handleUpdatePlacement({ imgPanY: Math.max(-100, currentPanY - 5) })}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cbd5e1',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <NeutralIcons.ChevronUp size={13} />
                  </button>
                  <button
                    type="button"
                    title="Nudge Down"
                    onClick={() => handleUpdatePlacement({ imgPanY: Math.min(100, currentPanY + 5) })}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cbd5e1',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <NeutralIcons.ChevronDown size={13} />
                  </button>
                  <button
                    type="button"
                    title="Nudge Right"
                    onClick={() => handleUpdatePlacement({ imgPanX: Math.min(100, currentPanX + 5) })}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cbd5e1',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <NeutralIcons.ChevronRight size={13} />
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => handleUpdatePlacement({ imgPanX: 0, imgPanY: 0 })}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#94a3b8',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Center
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUpdatePlacement({ imgZoom: 1, imgPanX: 0, imgPanY: 0 })}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#94a3b8',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    data-testid="mobile-fit-crop-to-image-btn"
                    title="Fit crop to image proportions"
                    onClick={() => {
                      const varName = (activeImageElem?.imageVariable || '').replace(/^\$/, '');
                      const currentSrc = (varName && formData?.[varName]) || activeImageElem?.src;
                      let aspect = null;
                      if (currentSrc) {
                        aspect = (globalImageAspectCache && globalImageAspectCache.get(currentSrc)) || getIntrinsicAspectFromSrc(currentSrc);
                      }
                      if (!aspect && !activeImageElem?.imageVariable) {
                        aspect = activeImageElem?.imgAspect;
                      }
                      if (!aspect && !activeImageElem?.imageVariable && activeImageElem?.naturalWidth && activeImageElem?.naturalHeight) {
                        aspect = activeImageElem.naturalWidth / activeImageElem.naturalHeight;
                      }
                      if (aspect && aspect > 0) {
                        const currentMax = Math.max(activeImageElem?.width || 160, activeImageElem?.height || 120);
                        let newW, newH;
                        if (aspect >= 1) {
                          newW = Math.round(currentMax);
                          newH = Math.max(30, Math.round(currentMax / aspect));
                        } else {
                          newH = Math.round(currentMax);
                          newW = Math.max(30, Math.round(currentMax * aspect));
                        }
                        handleUpdatePlacement({
                          width: newW,
                          height: newH,
                          imgZoom: 1,
                          imgPanX: 0,
                          imgPanY: 0,
                          cropApplied: true,
                          imgAspect: aspect
                        });
                      } else {
                        handleUpdatePlacement({ imgZoom: 1, imgPanX: 0, imgPanY: 0 });
                      }
                    }}
                    style={{
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: '1px solid #0284c7',
                      background: 'rgba(56, 189, 248, 0.15)',
                      color: '#38bdf8',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Fit to Image
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setIsPlacingPicture(false)}
                  style={{
                    flex: 1,
                    maxWidth: '120px',
                    background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 2px 10px rgba(0, 198, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <NeutralIcons.Check size={13} />
                  <span>Done</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              {currentPageIndex > 0 ? (
                <button
                  onClick={handleBackStep}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: isPreviewPage ? 'rgba(56, 189, 248, 0.12)' : 'rgba(255,255,255,0.06)',
                    border: isPreviewPage ? '1px solid rgba(56, 189, 248, 0.4)' : '1px solid rgba(255,255,255,0.12)',
                    color: isPreviewPage ? '#38bdf8' : '#cbd5e1',
                    borderRadius: '8px',
                    padding: '11px 8px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <NeutralIcons.ArrowLeft size={13} style={{ flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{isPreviewPage ? 'Back to Form' : 'Back'}</span>
                </button>
              ) : (
                <div style={{ flex: 1, minWidth: 0 }} />
              )}

              {!isPreviewPage ? (
                <button
                  onClick={handleNextStep}
                  style={{
                    flex: 1.2,
                    minWidth: 0,
                    background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '11px 8px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentPageIndex === totalPages - 1 ? 'Preview 3D Card' : 'Next Step'}
                  </span>
                  <NeutralIcons.ArrowRight size={13} style={{ flexShrink: 0 }} />
                </button>
              ) : (
                <>
                  {isPreviewPage && !isFlipped && (
                    <button
                      type="button"
                      onClick={() => setIsPlacingPicture(true)}
                      style={{
                        flex: 1.1,
                        minWidth: 0,
                        background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.2) 0%, rgba(14, 165, 233, 0.2) 100%)',
                        border: '1.5px solid #38bdf8',
                        color: '#38bdf8',
                        borderRadius: '8px',
                        padding: '11px 8px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 10px rgba(56, 189, 248, 0.25)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      <NeutralIcons.Image size={14} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Place Picture</span>
                    </button>
                  )}

                  <button
                    onClick={() => setShowConfirmModal(true)}
                    style={{
                      flex: 1.4,
                      minWidth: 0,
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '11px 8px',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Finalize & Issue</span>
                    <NeutralIcons.Lock size={13} style={{ flexShrink: 0 }} />
                  </button>
                </>
              )}
            </>
          )}

        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '16px'
        }}>
          <div style={{
            background: '#131824',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '16px',
            padding: '24px 18px',
            maxWidth: '360px',
            width: '100%',
            boxSizing: 'border-box',
            textAlign: 'center',
            boxShadow: '0 20px 50px rgba(0,0,0,0.6)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: '#10b981' }}>
              <NeutralIcons.Lock size={36} />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', color: '#f8fafc' }}>
              Confirm Finalization
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#94a3b8', lineHeight: 1.4 }}>
              Are you sure you want to finalize and issue Card #{formData?.card_number_issued || coin?.card_data?.card_number_issued || coin?.coin_number}? Once sealed, the card data cannot be modified and cryptographic authenticity checksums will be permanently generated.
            </p>

            {issuingError && (
              <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.35)', color: '#fca5a5', padding: '8px', borderRadius: '6px', fontSize: '12px', marginBottom: '14px' }}>
                {issuingError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                disabled={issuingLoading}
                onClick={() => setShowConfirmModal(false)}
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#cbd5e1',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                disabled={issuingLoading}
                onClick={handleFinalizeAndIssue}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {issuingLoading ? 'Sealing...' : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <span>Yes, Finalize</span>
                    <NeutralIcons.Check size={13} />
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interactive Map Coordinate Picker Modal */}
      {activeMapField && (
        <MapCoordinatePicker
          isOpen={!!activeMapField}
          fieldName={activeMapField.label || activeMapField.key}
          initialValue={formData[activeMapField.key] || ''}
          format={activeMapField.validation?.format || 'dd'}
          onClose={() => setActiveMapField(null)}
          onSelect={handleSelectMapCoordinates}
        />
      )}
    </div>
  );
}
